import "./styles.css";
import { supabase, setRememberMe } from "./supabaseClient.js";
import { initRenderer, renderScene, renderToCanvas, renderElementIcon, setRenderMode, exportPng, getPngDataUrl, canvasToField, fieldToCanvas, PLAYER_RADIUS } from "./render.js";
import { recognizedTemplate, specificTemplate, guessSportExplicit, sanitizeUltimateScene } from "./parser.js";
import { sceneFromAnalysis, routeDecision, applyHalfCourt, applyStadeTerrain, fitAgresToAction, enforceTableExterior, dropStillMoves, renumberOrders, applyStrikeApproach, applyReplacement, completeRoster, addPostedTeacher, addNamedGear, addDescribedKeeper, addDefenderMove, sanitizeScene } from "./analysis.js";
import { enforceRegulation } from "./regulation.js";
import { fidelityIssues, correctionPrompt, enforceShotDepth, enforceBackwardPass, enforceStrikeOrigin, enforceShotTarget, enforceShotOrigin, enforceCrossedRuns } from "./fidelity.js";
import { sceneGate, isValidSceneResult, repairIsBetter, repairMode, pickBestScene } from "./scenemode.js";
import { buildFeedbackRecord } from "./feedback.js";
import { initUnderstanding, showUnderstanding } from "./understanding.js";
import { createEditor, isConeMarker, isNoteMarker, isStationMarker, hasEditableText, ELEMENT_LIBRARY, libraryLabel } from "./editor.js";
import { createHistory } from "./history.js";
import { createProjection } from "./projection.js";
import { loadAds, mountAdCarousel } from "./ads.js";
import { renderDashboard } from "./admin.js";
import { confirmDialog, alertDialog } from "./ui.js";
import "./hero-preview.js";   // aperçu Couleur/Trait/Diaporama du hero (auto-init)
import "./lp-reveal.js";     // apparition au défilement des sections landing (auto-init)
import "./lp-terrain.js";    // mini-terrain animé de la section « Sports » (auto-init)

/* ============================================================================
   ÉTAT GLOBAL
   ========================================================================== */
const state = {
  user: null,
  lastJson: null,
  activeSport: "football",
  authMode: "signin",       // "signin" | "signup" | "recovery"
  recovering: false,        // réinitialisation de mot de passe en cours (lien e-mail)
  kind: "exercise",         // segmented Match/Exercice -> override du type (défaut : Exercice)
  kindTouched: false,       // l'utilisateur a-t-il forcé le type ?
  renderMode: "color",      // "color" | "line"
  simpleMode: true,         // vue allégée par défaut (cap joueurs + actions secondaires atténuées)
  editing: false,           // mode édition complet (palette d'ajout/suppression)
  editor: null,             // contrôleur d'édition canevas (editor.js)
  projection: null,         // contrôleur du mode projection (projection.js)
  prevSimple: true,         // mémorise le mode d'affichage avant l'édition
  sceneView: null,          // scène effectivement rendue (vue dérivée de lastJson)
  zoom: 1,
  view: "editor",           // "editor" | "library"
  source: "edge",           // origine du dernier rendu : edge | hybrid | fallback
  currentSchemaId: null,    // id du schéma en cours d'édition (null = nouveau)
  libraryRows: [],
  isAdmin: false,           // plan « admin » -> accès au Dev Dashboard
  analysis: null,           // { analyse, sport, text } : panneau « Ce que j'ai compris »
  hasPeda: false,           // la fiche pédagogique a-t-elle du contenu ?
  rightTab: "peda",         // onglet actif de la colonne de droite : "peda" | "edit"
  pedaEditing: false,       // formulaire d'édition de la fiche ouvert ?
  libScope: "mine",         // bibliothèque affichée : "mine" | "community"
  communityRows: [],        // situations approuvées de la bibliothèque commune
  sharedSourceIds: new Set(), // ids des schémas perso DÉJÀ proposés (pending/approved)
  quota: null,              // dernier get_quota_status() : plan effectif + plafonds
};

const $ = (id) => document.getElementById(id);
/* Plafond de bibliothèque : valeur de repli côté client. La VRAIE limite vient
   du serveur (plans.schema_limit via get_quota_status) — 10 pour un compte
   standard, 30 pour un compte coordo-EPS — et le trigger enforce_schema_cap
   l'applique de toute façon en base. */
const SCHEMA_LIMIT_DEFAULT = 10;
const schemaLimit = () => Number(state.quota?.schema_limit) || SCHEMA_LIMIT_DEFAULT;
const isCoordo = () => !!state.quota?.is_coordo;

/* SSO entrant depuis coordo-EPS (voir CLAUDE.md § SSO). */
const COORDO_URL = "https://coordo-eps.com";
const COORDO_FROM_KEY = "st_from_coordo";   // mémorise l'arrivée via /sso?from=coordo
/* Valeurs d'AFFICHAGE seulement (« un compte coordo donne droit à… ») : les
   quotas qui font foi sont ceux de la table `plans`, appliqués côté serveur. */
const COORDO_QUOTA_HINT = 60;
const COORDO_SCHEMA_HINT = 30;

/* Historique d'édition (annuler/rétablir) : clichés de la scène à chaque geste. */
const history = createHistory();

/* Grand écran = la colonne de droite existe (sinon la fiche passe sous le
   terrain et les propriétés de sélection retournent dans la palette). Doit
   refléter le breakpoint CSS `@media (max-width: 1100px)`. */
const deskMq = window.matchMedia("(min-width: 1101px)");

/* ============================================================================
   1) AUTHENTIFICATION (email + mot de passe)
   ========================================================================== */
function showAuthError(msg) { const e = $("authError"); e.textContent = msg; e.hidden = false; $("authInfo").hidden = true; }
function showAuthInfo(msg) { const e = $("authInfo"); e.textContent = msg; e.hidden = false; $("authError").hidden = true; }
function clearAuthMsg() { $("authError").hidden = true; $("authInfo").hidden = true; }

function setAuthMode(mode) {
  state.authMode = mode;
  const isSignup = mode === "signup";
  const isRecovery = mode === "recovery";   // saisie d'un nouveau mot de passe (lien e-mail)
  $("authTitle").textContent = isRecovery ? "Nouveau mot de passe" : isSignup ? "Créer un compte" : "Connexion";
  $("authSub").textContent = isRecovery
    ? "Choisis un nouveau mot de passe pour ton compte."
    : isSignup ? "Réservé aux enseignants — protection des coûts d'IA."
    : "Accédez à votre espace enseignant.";
  $("authSubmit").textContent = isRecovery ? "Enregistrer le mot de passe" : isSignup ? "Créer mon compte" : "Se connecter";
  $("authToggleBtn").textContent = isSignup ? "J'ai déjà un compte" : "Créer un compte enseignant";
  $("authPassword").autocomplete = (isSignup || isRecovery) ? "new-password" : "current-password";
  $("authPassword").placeholder = isRecovery ? "Nouveau mot de passe (6 caractères min.)" : "••••••••";
  // En mode récupération : pas d'e-mail, pas d'options « se souvenir / créer un compte / essai ».
  $("emailField").hidden = isRecovery;
  $("authAlt").hidden = isRecovery;
  $("loginExtras").style.display = (isSignup || isRecovery) ? "none" : "flex";
  clearAuthMsg();
}

async function handleAuthSubmit() {
  clearAuthMsg();
  const password = $("authPassword").value;

  // Réinitialisation : pas d'e-mail, on remplace le mot de passe de la session
  // de récupération (ouverte via le lien reçu par e-mail), puis on entre.
  if (state.authMode === "recovery") {
    if (password.length < 6) return showAuthError("Le mot de passe doit faire au moins 6 caractères.");
    $("authSubmit").disabled = true;
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      state.recovering = false;
      $("authPassword").value = "";
      setAuthMode("signin");
      const { data } = await supabase.auth.getSession();
      applySession(data.session);   // session valide -> ouverture de l'application
    } catch (err) {
      showAuthError(translateAuthError(err?.message || String(err)));
    } finally {
      $("authSubmit").disabled = false;
    }
    return;
  }

  const email = $("authEmail").value.trim();
  if (!email || !password) return showAuthError("Email et mot de passe requis.");
  if (password.length < 6) return showAuthError("Le mot de passe doit faire au moins 6 caractères.");

  // Préférence « se souvenir de moi » prise en compte AVANT l'écriture de session.
  setRememberMe($("rememberCheck").checked);

  $("authSubmit").disabled = true;
  try {
    if (state.authMode === "signup") {
      // `emailRedirectTo` explicite : sans lui, le lien de confirmation pointe
      // vers la « Site URL » du projet Supabase, la même pour tous les
      // environnements. Avec, l'e-mail ramène TOUJOURS sur l'origine d'où
      // l'inscription a été faite (production en prod, localhost en dev).
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin + "/" },
      });
      if (error) throw error;
      // Pas de session immédiate = confirmation par e-mail requise. On bascule
      // en mode connexion PUIS on affiche le message (setAuthMode efface les
      // messages : l'ordre compte, sinon l'info disparaît aussitôt).
      if (!data.session) {
        setAuthMode("signin");
        showAuthInfo("Compte créé ✅ Un e-mail de confirmation vient de t'être envoyé : clique sur le lien pour activer ton compte, puis connecte-toi ici. (Pense à vérifier tes spams.)");
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    }
  } catch (err) {
    showAuthError(translateAuthError(err?.message || String(err)));
  } finally {
    $("authSubmit").disabled = false;
  }
}

async function handleForgotPassword(e) {
  e.preventDefault();
  clearAuthMsg();
  const email = $("authEmail").value.trim();
  if (!email) return showAuthError("Saisis ton adresse e-mail pour recevoir le lien de réinitialisation.");
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    if (error) throw error;
    showAuthInfo("Si un compte existe pour cette adresse, un e-mail de réinitialisation vient d'être envoyé.");
  } catch (err) {
    showAuthError(translateAuthError(err?.message || String(err)));
  }
}

function translateAuthError(msg) {
  const m = msg.toLowerCase();
  if (m.includes("invalid login")) return "Email ou mot de passe incorrect.";
  if (m.includes("already registered")) return "Cet email a déjà un compte. Connecte-toi.";
  if (m.includes("email not confirmed")) return "Email non confirmé : vérifie ta boîte mail.";
  return msg;
}

async function handleLogout() { await supabase.auth.signOut(); }

/* ----------------------------------------------------------------------------
   CONNEXION PAR LIEN MAGIQUE (signInWithOtp)
   Nécessaire pour les comptes provisionnés depuis coordo-EPS : ils n'ont PAS
   de mot de passe. `shouldCreateUser: false` garantit que ce chemin ne crée
   jamais de compte — l'inscription reste le formulaire existant.
   -------------------------------------------------------------------------- */
async function handleMagicLink() {
  clearAuthMsg();
  const email = $("authEmail").value.trim();
  if (!email) return showAuthError("Saisis ton adresse e-mail pour recevoir le lien de connexion.");
  setRememberMe($("rememberCheck").checked);
  const btn = $("magicLinkBtn");
  btn.disabled = true;
  // Message NEUTRE : il ne révèle pas si l'adresse a un compte.
  const neutral = "Si un compte existe pour cette adresse, un lien de connexion vient d'être envoyé. "
    + "Ouvre-le depuis ce navigateur (pense à vérifier tes spams).";
  try {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin + "/", shouldCreateUser: false },
    });
    if (error) throw error;
    showAuthInfo(neutral);
  } catch (err) {
    const m = String(err?.message || err).toLowerCase();
    // Compte inexistant / OTP désactivé pour les inscriptions : même réponse.
    if (m.includes("signups not allowed") || m.includes("not found") || m.includes("user not found")) {
      showAuthInfo(neutral);
    } else {
      showAuthError(translateAuthError(err?.message || String(err)));
    }
  } finally {
    btn.disabled = false;
  }
}

/* ----------------------------------------------------------------------------
   SSO ENTRANT depuis coordo-EPS — route publique /sso
   coordo (projet Supabase SÉPARÉ) appelle l'Edge Function `sso-provision`
   serveur à serveur, récupère un `token_hash` à usage unique et redirige
   l'utilisateur vers  /sso?token_hash=...&from=coordo .
   Ici, on échange ce jeton contre une session Supabase, puis on NETTOIE l'URL
   (replaceState : le jeton ne reste ni dans la barre d'adresse ni dans
   l'historique). Aucun impact sur le parcours d'inscription/connexion normal.
   -------------------------------------------------------------------------- */
const isSsoRoute = () => location.pathname.replace(/\/+$/, "").toLowerCase() === "/sso";

function ssoFailure(msg) {
  state.ssoPending = false;
  $("ssoPending").hidden = true;
  $("ssoFailed").hidden = false;
  if (msg) $("ssoErrorMsg").textContent = msg;
  let fromCoordo = false;
  try { fromCoordo = localStorage.getItem(COORDO_FROM_KEY) === "1"; } catch (_) { /* ignore */ }
  const back = $("ssoBackCoordo");
  back.href = COORDO_URL;
  back.hidden = !fromCoordo;
}

async function handleSsoEntry() {
  state.ssoPending = true;
  $("ssoView").hidden = false;
  $("authView").hidden = true;
  $("appView").hidden = true;

  const params = new URLSearchParams(location.search);
  const tokenHash = params.get("token_hash");
  // `from=coordo` : on mémorise l'origine pour afficher le bouton de retour.
  if (params.get("from") === "coordo") {
    try { localStorage.setItem(COORDO_FROM_KEY, "1"); } catch (_) { /* ignore */ }
  }

  if (!tokenHash) {
    ssoFailure("Lien d'accès incomplet. Reconnecte-toi depuis coordo-EPS ou connecte-toi directement.");
    return;
  }

  setRememberMe(true);   // la session issue du SSO persiste comme une connexion normale
  try {
    // Le jeton vient d'un lien magique : selon la version de GoTrue il se
    // vérifie avec le type générique « email » ou avec « magiclink ». On tente
    // le premier, puis l'autre — un jeton expiré échoue de toute façon dans les
    // deux cas et tombe sur l'écran d'erreur.
    let { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "email" });
    if (error || !data?.session) {
      ({ data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "magiclink" }));
    }
    if (error || !data?.session) throw error || new Error("no_session");
    // Nettoyage de l'URL AVANT d'ouvrir l'app : le jeton disparaît de la barre
    // d'adresse ET de l'historique (replaceState, pas pushState).
    // NB : `history` désigne ici l'historique d'ÉDITION (createHistory) — il
    // faut donc passer explicitement par window.history.
    window.history.replaceState(null, "", "/");
    state.ssoPending = false;
    $("ssoView").hidden = true;
    applySession(data.session);
  } catch (err) {
    console.warn("[sso] échange du jeton impossible :", err?.message || err);
    ssoFailure();
  }
}

/* ----------------------------------------------------------------------------
   ESSAI SANS COMPTE — connexion ANONYME Supabase : un vrai utilisateur
   (rôle authenticated, is_anonymous=true) au plan « trial » (1 génération),
   donc le quota est appliqué CÔTÉ SERVEUR comme pour tout le monde.
   -------------------------------------------------------------------------- */
const isTrial = () => !!state.user?.is_anonymous;

async function startTrial() {
  clearAuthMsg();
  $("trialBtn").disabled = true;
  try {
    setRememberMe(true);   // la session d'essai persiste : 1 essai par navigateur
    const { error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
  } catch (err) {
    const m = String(err?.message || err).toLowerCase();
    showAuthError(m.includes("anonymous")
      ? "L'essai sans compte n'est pas encore activé. Crée un compte gratuit pour commencer."
      : translateAuthError(err?.message || String(err)));
  } finally {
    $("trialBtn").disabled = false;
  }
}

/* « Créer mon compte » depuis le bandeau d'essai : on clôt la session anonyme
   et on ouvre directement le formulaire d'inscription. */
async function trialToSignup() {
  await supabase.auth.signOut();
  setAuthMode("signup");
}

/* Vide TOUT l'état lié à l'utilisateur (scène en cours, bibliothèque, analyse)
   et repeint un terrain vierge. Appelé quand l'utilisateur connecté CHANGE,
   pour qu'un compte ne voie jamais le contenu d'un autre. */
function resetUserScene() {
  state.lastJson = null;
  state.sceneView = null;
  state.currentSchemaId = null;
  state.analysis = null;
  state.libraryRows = [];
  state.kindTouched = false;
  if ($("prompt")) $("prompt").value = "";
  resetEditorChrome();
  showUnderstanding(null);
  closeFicheEditSilently();
  refreshMeta();
  renderPedagogy({});
  history.reset(null);
  updateUndoUi();
  paint();
}

/* Réinitialisation de mot de passe en cours (lien e-mail cliqué) : on RESTE sur
   l'écran d'auth, en mode « nouveau mot de passe », sans ouvrir l'application —
   même si Supabase a ouvert une session de récupération. */
function showRecovery(session) {
  state.user = session?.user || null;
  $("authView").hidden = false;
  $("appView").hidden = true;
  if (state.authMode !== "recovery") {
    setAuthMode("recovery");
    showAuthInfo("Lien validé ✅ Choisis ton nouveau mot de passe ci-dessous.");
  }
  // On retire le jeton de l'URL pour qu'un rechargement ne relance pas le flux.
  // `history` (module) = historique d'édition : on vise window.history.
  if (/type=recovery/.test(location.hash)) window.history.replaceState(null, "", location.pathname + location.search);
}

/* Bascule entre l'écran d'auth et l'application selon la session. */
function applySession(session) {
  // Échange de jeton SSO en cours : on ne bascule NI sur la landing NI sur
  // l'app tant que verifyOtp n'a pas tranché (sinon la landing clignote).
  if (state.ssoPending) return;
  if (state.recovering) { showRecovery(session); return; }
  // Changement d'utilisateur (connexion d'un autre compte, déconnexion) : on
  // purge l'état en mémoire. Un simple rafraîchissement de jeton (même id) ne
  // déclenche PAS la purge (sinon on viderait l'écran à chaque refresh).
  const prevId = state.user?.id || null;
  state.user = session?.user || null;
  if ((state.user?.id || null) !== prevId) resetUserScene();
  const logged = !!state.user;
  const trial = logged && isTrial();
  $("authView").hidden = logged;
  $("appView").hidden = !logged;
  $("logoutBtn").hidden = !logged;
  // En mode ESSAI (session anonyme), « Se déconnecter » n'a pas de sens : on
  // propose plutôt de créer un compte / se connecter.
  // IMPORTANT : ne PAS écraser le contenu du bouton via textContent — cela
  // détruirait l'icône SVG et le <span> de libellé (structure utilisée par le
  // header responsive : icône seule sur mobile, texte sur desktop). On met à
  // jour uniquement le libellé + le titre/aria-label (accessibilité mobile).
  const logoutLabel = trial ? "Créer un compte / Se connecter" : "Se déconnecter";
  const logoutBtn = $("logoutBtn");
  const logoutTxt = logoutBtn.querySelector(".appnav__logout-txt");
  if (logoutTxt) logoutTxt.textContent = logoutLabel;
  else logoutBtn.textContent = logoutLabel;   // repli si structure absente
  logoutBtn.title = logoutLabel;
  logoutBtn.setAttribute("aria-label", logoutLabel);
  refreshDevTools();   // bouton « Exporter l'analyse IA » réservé à l'admin
  $("userEmail").textContent = logged ? (trial ? "Mode essai" : state.user.email) : "";
  // Mode essai : bandeau d'invitation, pas de bibliothèque (sauvegarde réservée
  // aux comptes gratuits).
  $("trialBanner").hidden = !trial;
  $("navLibrary").hidden = trial;
  // Dev Dashboard : le lien n'apparaît que pour le plan « admin » (le profil
  // est lu en base ; la vraie protection est côté serveur, RLS + RPC).
  state.isAdmin = false;
  $("navDash").hidden = true;
  if (logged && !trial) {
    supabase.from("profiles").select("plan_id").eq("id", state.user.id).single()
      .then(({ data }) => {
        state.isAdmin = data?.plan_id === "admin";
        $("navDash").hidden = !state.isAdmin;
        refreshPendingBadge();   // notification : propositions en attente
      });
  }
  if (logged) {
    $("appAvatar").textContent = trial ? "★" : initials(state.user.email);
    showView("editor");
    refreshQuota();
    refreshAds();
  } else {
    $("quotaBadge").textContent = "—";
    state.quota = null;
  }
  applyCoordoUi();
}

/* Recharge les carrousels publicitaires depuis la table `ads` (appelé à la
   connexion et après toute modification dans le dashboard admin). */
async function refreshAds() {
  const ads = await loadAds();
  mountAdCarousel($("adEditor"), ads);
  mountAdCarousel($("adLibrary"), ads);
}
function initials(email) {
  const base = (email || "").split("@")[0].replace(/[^a-zA-Z]/g, "");
  return (base.slice(0, 2) || "EP").toUpperCase();
}

/* ============================================================================
   2) QUOTA
   ========================================================================== */
async function refreshQuota() {
  const { data, error } = await supabase.rpc("get_quota_status");
  if (error) {
    console.warn("[quota] get_quota_status a échoué :", error.message || error, error);
    $("quotaBadge").textContent = "quota ?";
    return;
  }
  const q = Array.isArray(data) ? data[0] : data;
  if (!q) return;
  // Profil de quota effectif (plan, plafond bibliothèque, marqueur coordo) :
  // calculé CÔTÉ SERVEUR à partir de user_metadata.source.
  state.quota = q;
  updateQuota(q.used, q.quota);
  applyCoordoUi();
}

/* Mentions liées au compte coordo-EPS : étiquette « quota étendu » à côté du
   badge, et bouton de retour vers coordo si l'utilisateur en vient. */
function applyCoordoUi() {
  const tag = $("coordoQuotaTag");
  if (tag) tag.hidden = !isCoordo();
  const back = $("coordoBackBtn");
  if (back) {
    let fromCoordo = false;
    try { fromCoordo = localStorage.getItem(COORDO_FROM_KEY) === "1"; } catch (_) { /* ignore */ }
    back.href = COORDO_URL;
    back.hidden = !fromCoordo || !state.user;
  }
}

function updateQuota(used, quota) {
  used = Number(used) || 0; quota = Number(quota) || 0;
  const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  $("quotaBadge").textContent = `${used} / ${quota}`;
  $("quotaBadge").title = isCoordo()
    ? `Générations utilisées ce mois-ci — quota étendu grâce à ton compte coordo-EPS`
    : "Générations utilisées ce mois-ci";
  $("quotaFillHead").style.width = pct + "%";
  // Ligne quota secondaire (peut être absente : le badge d'en-tête fait foi).
  // « générations », pas « schémas » : le compteur mesure les appels IA du mois
  // calendaire (il repart à zéro le 1er), pas la bibliothèque. L'ancien libellé
  // faisait croire à une perte de schémas à chaque début de mois.
  const lineText = $("quotaLineText"); if (lineText) lineText.textContent = `${used} / ${quota} générations ce mois`;
  const lineFill = $("quotaLineFill"); if (lineFill) lineFill.style.width = pct + "%";
}

/* Message unique de quota épuisé : pour un compte standard, il indique
   coordo-EPS comme moyen d'obtenir un quota supérieur. */
function quotaExceededMessage(quota) {
  const n = Number(quota) || 0;
  return isCoordo()
    ? `Quota mensuel atteint (${n} générations, quota étendu coordo-EPS). Réessaie le mois prochain.`
    : `Quota mensuel atteint (${n} générations). Réessaie le mois prochain — ou passe par coordo-EPS `
      + `(${COORDO_URL}) : un compte coordo-EPS donne droit à ${COORDO_QUOTA_HINT} générations par mois `
      + `et ${COORDO_SCHEMA_HINT} schémas stockés.`;
}

/* ============================================================================
   3) GÉNÉRATION
   ========================================================================== */
function showError(msg) { const b = $("errorBox"); b.textContent = msg; b.classList.add("active"); }

/* OUTIL DEV (admin uniquement) : exporte la dernière réponse IA brute, pour la
   transformer en fixture de test (rejeu du chemin IA hors-ligne). */
const DEV_ADMIN_EMAIL = "delorenzo.lionel@orange.fr";
function refreshDevTools() {
  const btn = $("exportAiBtn"); if (!btn) return;
  const allowed = (state.user?.email || "").toLowerCase() === DEV_ADMIN_EMAIL;
  btn.hidden = !allowed;
  btn.disabled = !state.lastAiResult;
}
function exportAiAnalysis() {
  const btn = $("exportAiBtn");
  if (!state.lastAiResult) return;
  const json = JSON.stringify(state.lastAiResult, null, 2);
  console.log("[TactiQ] Fixture IA :\n" + json);
  const done = () => { if (btn) { const t = btn.textContent; btn.textContent = "✓ Copié"; setTimeout(() => { btn.textContent = t; }, 1500); } };
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(json).then(done, done);
  else done();
}
function clearError() { $("errorBox").classList.remove("active"); }
/* Actions qui portent sur LE SCHÉMA AFFICHÉ (éditer, projeter, exporter,
   enregistrer). Tant qu'une génération court, le schéma à l'écran est
   PROVISOIRE — soit un aperçu de gabarit, soit le schéma précédent : agir
   dessus produit un travail perdu (l'édition est écrasée par finishRender) ou
   un export du mauvais schéma. On les verrouille le temps de la génération ;
   le bouton « Générer » l'est déjà. */
function lockSchemaActions(on) {
  for (const id of ["editToggle", "projectBtn", "exportBtn", "saveBtn"]) {
    const b = $(id); if (b) b.disabled = on;
  }
  if (on) { const m = $("exportMenu"); if (m) m.hidden = true; }
}
function setLoading(on) {
  $("loader").classList.toggle("active", on);
  $("generateBtn").disabled = on;
  lockSchemaActions(on);
  if (!on) genHide();   // fin de génération (succès, erreur ou repli local) : plus d'indicateur sur le canevas
}
/* Message du loader (change pendant l'hydratation d'un aperçu immédiat). */
function loaderText(msg) { const el = $("loader").querySelector("span"); if (el) el.textContent = msg; }

/* ----------------------------------------------------------------------------
   INDICATEUR DE GÉNÉRATION SUR LE CANEVAS
   Le loader de gauche reste (miroir discret), mais l'attente vit LÀ OÙ LE
   REGARD EST : sur le schéma. Deux états exclusifs :
   - genOverlay(step, msg) : voile + étapes (Analyse / Dessin / Contrôle),
     tant qu'aucune scène n'est servie — sa disparition = « c'est terminé » ;
   - genChip(msg) : pastille en coin quand un schéma UTILISABLE est déjà
     affiché (aperçu gabarit) — on ne voile JAMAIS un schéma lisible.
   genStep() route vers l'un ou l'autre selon qu'un aperçu a été rendu.
   -------------------------------------------------------------------------- */
let genPreviewShown = false;   // un aperçu utilisable est-il déjà à l'écran ?

function genOverlay(step, msg) {
  const ov = $("genOverlay"); if (!ov) return;
  const chip = $("genChip"); if (chip) chip.hidden = true;
  ov.hidden = false;
  ov.querySelectorAll(".gen-steps li").forEach((li, i) => {
    li.classList.toggle("is-current", i === step);
    li.classList.toggle("is-done", i < step);
  });
  const m = $("genOverlayMsg"); if (m) m.textContent = msg;
}
function genChip(msg) {
  const chip = $("genChip"); if (!chip) return;
  const ov = $("genOverlay"); if (ov) ov.hidden = true;
  chip.hidden = false;
  const m = $("genChipMsg"); if (m) m.textContent = msg;
}
function genHide() {
  const ov = $("genOverlay"); if (ov) ov.hidden = true;
  const chip = $("genChip"); if (chip) chip.hidden = true;
}
function genStep(step, msg) { if (genPreviewShown) genChip(msg); else genOverlay(step, msg); }

/* ----------------------------------------------------------------------------
   POLISSAGE EN ARRIÈRE-PLAN (retours prod 👎 « beaucoup trop long »)
   Une scène acceptée dont les seuls défauts sont de LISIBILITÉ est servie tout
   de suite ; la réparation part ici, sans bloquer. À l'arrivée, la version
   polie ne remplace l'affichage QUE si :
   - elle est STRICTEMENT meilleure (même règle que la réparation classique) ;
   - aucune génération n'a démarré entre-temps (compteur de séquence) ;
   - l'utilisateur n'est PAS en train d'éditer ni de projeter ;
   - le schéma affiché est resté BIT À BIT celui qu'on a servi (instantané
     JSON) — la moindre retouche utilisateur rend le polissage caduc.
   Sinon la version polie est simplement abandonnée : c'était un bonus. */
/* DISJONCTEUR DU MODE SCÈNE : après un `ai_timeout` (DeepSeek au ralenti), les
   générations suivantes partent DIRECTEMENT sur le pipeline classique pendant
   10 minutes — un seul appel, ~40 s, au lieu de re-payer 80 s de timeout à
   chaque essai. Le retour prod « mes deux derniers tests, inutilisable » était
   exactement ça : deux timeouts consécutifs pendant un épisode lent. */
const SCENE_COOLDOWN_MS = 10 * 60_000;
const sceneCooldownActive = () => Date.now() < (+localStorage.getItem("st_scene_cooldown") || 0);
const tripSceneCooldown = () => { try { localStorage.setItem("st_scene_cooldown", String(Date.now() + SCENE_COOLDOWN_MS)); } catch (_) {} };

let polishSeq = 0;   // incrémenté à CHAQUE generate() — invalide les polissages en vol
async function launchScenePolish({ scene, gate, text, sportOverride, sportScene, rootGenId }) {
  const mySeq = polishSeq;
  const snapshot = JSON.stringify(state.lastJson);
  genChip("L'IA peaufine la lisibilité… (le schéma est utilisable)");
  try {
    const { data: dR } = await supabase.functions.invoke("generate", {
      body: { prompt: `${promptContext()}\n\n${text}`, sportOverride, mode: "scene",
        generation_id: rootGenId,
        repair: { previous: scene, issues: [...gate.grave, ...gate.soft] } },
    });
    if (dR?.usage) updateQuota(dR.usage.used, dR.usage.quota);
    const rep = dR?.data;
    if (!isValidSceneResult(rep)) return;
    // Même règle qu'à la génération : on juge la scène telle qu'elle sera
    // dessinée, sinon le polissage se compare à un objet qui n'existe pas.
    { const sp = resolveSport(rep.sport || sportOverride || sportScene);
      try { if (sp === "tennis_de_table") enforceTableExterior(rep); } catch (_) {}
      try { applyStrikeApproach(rep, sp, text); } catch (_) {}
      try { applyReplacement(rep, sp, text); } catch (_) {}
      try { enforceCrossedRuns(rep, sp, text); } catch (_) {}
      try { enforceRegulation(rep, sp, text); } catch (_) {}
      try { enforceShotDepth(rep, sp, text); } catch (_) {}
      try { enforceBackwardPass(rep, sp, text); } catch (_) {}
      try { enforceStrikeOrigin(rep, sp, text); } catch (_) {}
      try { enforceShotOrigin(rep, sp, text); } catch (_) {}
      try { enforceShotTarget(rep, sp, text); } catch (_) {}
      try { dropStillMoves(rep); renumberOrders(rep); } catch (_) {}
      try { applyStadeTerrain(rep, sp, text); } catch (_) {}
      try { fitAgresToAction(rep, sp, text); } catch (_) {}
      // Les cinq correcteurs d'AJOUT, comme à la génération : sans eux, la
      // version polie serait jugée sur une scène AMPUTÉE de ses renforts et
      // perdrait la comparaison qu'elle devait gagner.
      try {
        completeRoster(rep, sp, text);
        addPostedTeacher(rep, sp, text);
        addNamedGear(rep, sp, text);
        addDescribedKeeper(rep, sp, text);
        addDefenderMove(rep, sp, text);
        enforceRegulation(rep, sp, text);
      } catch (_) {} }
    const g2 = sceneGate(rep, text, sportOverride || rep.sport || sportScene);
    if (!g2.accept || !repairIsBetter(gate, g2)) return;
    // Garde-fous d'actualité : générations, édition, projection, retouches.
    if (mySeq !== polishSeq || state.editing) return;
    const proj = $("projection"); if (proj && !proj.hidden) return;
    if (JSON.stringify(state.lastJson) !== snapshot) return;
    // ⚠️ Bug trouvé par relecture (10/08) : ce chemin appelait `forceSport`,
    // une const LOCALE à generate() — ReferenceError à CHAQUE polissage
    // abouti, avalée par le catch ci-dessous. La version polie était donc
    // TOUJOURS jetée, après avoir consommé l'appel `+repair+soft`. Même
    // logique, avec ce que cette fonction reçoit : l'override prime, sinon le
    // sport de la scène servie (sportScene = sportOverride || textSport ||
    // scene.sport, calculé par l'appelant).
    if (sportOverride) rep.sport = sportOverride;
    else if (sportScene) rep.sport = sportScene;
    applyHalfCourt(rep, text, rep.sport);
    rep.title = rep.title || scene.title;
    rep.pedagogy = rep.pedagogy || scene.pedagogy;
    applyMeta(rep);
    state.currentSchemaId = null;
    finishRender(rep, "scene");
  } catch (e) { console.warn("Polissage arrière-plan indisponible :", e); }
  finally { if (mySeq === polishSeq) genHide(); }
}
function resolveSport(detected) {
  const sel = $("sportSelect").value;
  return sel === "auto" ? (detected || "football") : sel;
}
/* Contexte injecté dans le prompt : type, niveau, effectif (orientation IA). */
function promptContext() {
  const typ = state.kind === "exercise" ? "situation d'apprentissage (exercice)" : "schéma de match";
  return `Contexte EPS — Type attendu : ${typ}.`;
}
/* Type : détection auto par défaut, forcé seulement si l'utilisateur a touché
   le segmenté. Reflète ensuite l'état dans l'UI. */
function resolveKind(detected) {
  const d = detected === "exercise" ? "exercise" : "match";
  state.kind = state.kindTouched ? state.kind : d;
  setSegActive("segKind", "data-kind", state.kind);
  return state.kind;
}
function applyMeta(result, text) {
  result.kind = resolveKind(result.kind);
  // PRÉSERVE le meta existant (halfCourt notamment) : n'écrase que renderMode.
  result.meta = { ...(result.meta || {}), renderMode: state.renderMode };
  sanitizeUltimateScene(result, text != null ? text : state.lastText);
  // Fond de terrain STADE (athlétisme) : posé pour TOUTES les routes, y compris
  // la scène directe qui ne traverse pas routeDecision.
  fitAgresToAction(result, resolveSport(result.sport), text != null ? text : state.lastText);
  applyStadeTerrain(result, resolveSport(result.sport), text != null ? text : state.lastText);
  return result;
}

async function generate() {
  clearError();
  const text = $("prompt").value.trim();
  if (!text) return showError("Veuillez saisir une description.");
  state.lastText = text;

  const sportSel = $("sportSelect").value;
  const sportOverride = sportSel === "auto" ? null : sportSel;
  // En mode auto, si l'utilisateur nomme explicitement un sport, on l'impose
  // (corrige le sport quel que soit le retour de l'IA, sans redéploiement).
  const textSport = sportSel === "auto" ? guessSportExplicit(text) : null;
  const forceSport = (d) => { if (sportOverride) d.sport = sportOverride; else if (textSport) d.sport = textSport; };
  // Le moteur local reçoit les choix de l'utilisateur : le sport du menu et le
  // type Match/Exercice (s'il a été forcé) priment sur la détection texte.
  const parseOpts = { sport: sportOverride || textSport || undefined, kind: state.kindTouched ? state.kind : undefined };

  // Nouvelle génération = nouvelle feuille : on remet l'atelier d'édition à zéro
  // (mode édition fermé, sélection vidée, zoom 100 %) pour ne pas enchaîner sur
  // l'état du schéma précédent.
  resetEditorChrome();
  state.analysis = null;   // sera posé si l'IA fournit une analyse valide
  state.lastCorrected = false;   // retente exercée sur CETTE génération ?
  // UN CLIC = UN CRÉDIT : la première génération qui débite le quota devient la
  // RACINE. Tous les appels internes qui suivent (réparation, repli, retente)
  // la citent, et le serveur les offre après vérification en base.
  let rootGenId = null;
  polishSeq++;   // toute nouvelle génération invalide un polissage en vol
  setLoading(true);
  loaderText("Analyse en cours…");
  genPreviewShown = false;
  genStep(0, "Analyse de la description…");

  // APERÇU IMMÉDIAT (template-first) : quand un SET-PLAY SPÉCIFIQUE est reconnu,
  // le dessin ne dépend pas de l'IA — on l'affiche TOUT DE SUITE (latence perçue
  // ≈ 0) via routeDecision (mêmes overlays que le rendu final), et l'appel IA
  // n'apporte plus que titre/pédagogie/arbitrage. Le flux normal ci-dessous
  // ÉCRASE ensuite cet aperçu par le circuit INCHANGÉ : l'état final est
  // identique à celui d'avant cette optimisation. NB : un set-play reconnu
  // exclut la route « edge » (routeDecision) ET le mode scène (ci-dessous),
  // donc chaque chemin de réponse ré-écrase l'aperçu par une géométrie
  // ÉQUIVALENTE (analysis / template / hybrid / fallback).
  //
  // C'est la condition pour montrer un schéma AVANT la fin de la génération :
  // l'aperçu n'est affiché QUE s'il est déjà, géométriquement, le schéma
  // livré. Sur un gabarit générique (l'IA va dessiner la scène et l'écraser),
  // il n'y a PAS d'aperçu — le voile d'attente et ses étapes restent affichés
  // jusqu'au schéma définitif.
  const localTemplate = specificTemplate(text, parseOpts);
  if (localTemplate) {
    try {
      const { scene: preview } = routeDecision({ players: [] }, { text, sport: sportOverride || textSport || undefined, parseOpts });
      forceSport(preview);
      adoptLocalAnalysis(preview, text);
      applyMeta(preview);
      if (state.analysis) preview.meta.analyse = state.analysis.analyse;
      state.currentSchemaId = null;   // l'aperçu n'est pas le schéma ouvert depuis la bibliothèque
      finishRender(preview, "preview");
      loaderText("Schéma affiché — l'IA rédige le titre et la pédagogie…");
      genPreviewShown = true;
      genChip("L'IA rédige le titre et la fiche pédagogique…");
    } catch (e) { console.warn("Aperçu local indisponible :", e); }
  }

  try {
    // ── MODE « SCÈNE DIRECTE » (étape 3 de la bascule, src/scenemode.js) ────
    // L'IA dessine la scène complète ; les bancs (fidélité + règlement) la
    // jugent ICI : acceptée -> rendue telle quelle ; sinon UNE réparation
    // ciblée ; sinon on TOMBE dans le pipeline classique ci-dessous, inchangé.
    // Mesure lot doré : 591/682 à zéro défaut, aucun cas grave par
    // construction, scène directe servie ~96 % du temps.
    // Kill-switch sans redéploiement : localStorage.setItem("aiScene", "off").
    //
    // SET-PLAY SPÉCIFIQUE RECONNU -> PAS de mode scène. Le moteur local dessine
    // ces coups précis (pick and roll, rondo, 2-c-1…) mieux que l'IA et
    // INSTANTANÉMENT (c'est l'aperçu ci-dessus) ; le pipeline classique ne
    // redemande à l'IA que titre/pédagogie/arbitrage et re-sert le gabarit via
    // routeDecision. Laisser la scène directe courir ici faisait ÉCRASER un
    // rendu déterministe propre par une scène IA acceptable pour la garde mais
    // visuellement inférieure — après 1 à 2 min d'attente et au prix d'appels
    // IA inutiles.
    //
    // ⚠️ Cette porte ne doit JAMAIS redevenir `recognizedTemplate` : les
    // gabarits GÉNÉRIQUES (filet_echange, invasion_attaque… 407 des 695 prompts
    // du lot doré) ne sont qu'un équivalent grossier de l'analyse structurée —
    // les exclure du mode scène l'éteignait pour 84 % du trafic réel.
    // POURQUOI LE MODE SCÈNE N'A-T-IL PAS TOURNÉ ? Ces trois chemins le
    // sautaient SANS RIEN JOURNALISER : la table `route_stats` ne contenait
    // alors ni `scene` ni `scene_rejetee`, et la génération apparaissait comme
    // un simple `analysis`/`template` — impossible de distinguer « la porte
    // s'est fermée » de « le disjoncteur était armé ». Mesure du 05/08 : sur
    // 21 générations classiques, seules 2 venaient d'un refus de la garde ;
    // une dizaine n'avaient aucune trace. On journalise donc le MOTIF.
    if (localTemplate) logRoute("scene_ignoree_setplay", { prompt: text, template: localTemplate, sport: sportOverride || textSport || null, kind: state.kind });
    else if (sceneCooldownActive()) logRoute("scene_disjoncteur", { prompt: text, sport: sportOverride || textSport || null, kind: state.kind });
    if (!localTemplate && localStorage.getItem("aiScene") !== "off" && !sceneCooldownActive()) {
      try {
        loaderText("L'IA dessine la scène…");
        genStep(1, "L'IA dessine la scène…");
        const { data: dS, error: eS } = await supabase.functions.invoke("generate", {
          body: { prompt: `${promptContext()}\n\n${text}`, sportOverride, mode: "scene" },
        });
        if (eS) {
          const detail = await readFunctionError(eS);
          if (detail?.error === "quota_exceeded") {
            showError(quotaExceededMessage(detail.quota));
            updateQuota(detail.quota, detail.quota);
            return;
          }
          // Échec technique : le pipeline classique (et son repli local) prend
          // la suite — même chaîne de secours qu'avant la bascule.
          // Un TIMEOUT arme le disjoncteur : DeepSeek est au ralenti, inutile
          // de re-payer l'attente à chaque génération pendant l'épisode.
          if (detail?.error === "ai_timeout") tripSceneCooldown();
          console.warn("Mode scène en échec, pipeline classique :", detail ? JSON.stringify(detail) : "(pas de détail)");
          // Quatrième chemin muet, et le plus instructif : c'est ICI que naît
          // le disjoncteur. On distingue le TIMEOUT (DeepSeek au ralenti — le
          // seul motif qui coupe le mode scène pour 10 minutes) des autres
          // échecs, pour savoir lequel des deux coûte des générations.
          logRoute(detail?.error === "ai_timeout" ? "scene_timeout" : "scene_erreur",
            { prompt: text, sport: sportOverride || textSport || null, kind: state.kind });
        } else {
          if (dS?.generation_id) rootGenId = rootGenId || dS.generation_id;
          if (dS?.usage) updateQuota(dS.usage.used, dS.usage.quota);
          // MEILLEUR-DES-DEUX : l'Edge renvoie deux scènes tirées en parallèle
          // (sans raisonnement) — la garde départage ici, où vivent les juges.
          const sportHint = sportOverride || textSport || dS?.data?.sport;
          // LES JUGES DOIVENT VOIR LA SCÈNE QUI SERA DESSINÉE. Jusqu'ici la
          // garde tournait ICI et `applyMeta` plus bas : les trois juges
          // évaluaient donc la scène BRUTE de l'IA, alors qu'au repli ils
          // évaluent la scène PROJETÉE (finish() projette avant le garde-fou de
          // fidélité). Les deux routes ne jugeaient pas le même objet — c'est ce
          // clivage qui rendait inapplicable la règle « coureur sur la pelouse »
          // mesurée à la salve athlé nº2 : `terrainOpts.stade` n'existait pas
          // encore quand la garde passait. Les deux projections sont idempotentes
          // (drapeaux `stadePose` / `agresAjuste`), l'`applyMeta` plus bas les
          // retrouve donc sans les rejouer.
          // Les CORRECTEURS déterministes tournent ici aussi, dans l'ordre de
          // `finish()`. Le dépôt en possédait deux — `enforceTableExterior` et
          // `enforceRegulation` — et aucun n'était appelé sur cette route : le
          // juge SIGNALAIT « le déplacement finit sur la table » ou « serveur et
          // relanceur du même côté » pendant que le correcteur capable de les
          // réparer dormait au repli. Mesuré sur les 7 lots raquettes : ces
          // classes pèsent 7 des 32 rejets. Réparer vaut mieux que rejeter —
          // le repli appliquerait de toute façon la même correction.
          const projeter = (s) => {
            if (!s || typeof s !== "object") return s;
            const sp = resolveSport(s.sport || sportHint);
            try { if (sp === "tennis_de_table") enforceTableExterior(s); } catch (_) { /* la garde décide, pas la projection */ }
            // SIXIÈME ET SEPTIÈME EXEMPLAIRES DU MOTIF — `applyStrikeApproach`
            // et `applyReplacement` vivaient eux aussi dans `finish()`, donc au
            // repli seulement, et traitent deux classes de la table des rejets
            // (frappe fantôme, replacement au centre absent).
            // ILS VONT ENSEMBLE, ET DANS CET ORDRE (compagnons, comme
            // dropStillMoves+renumberOrders) : un replacement ramène le
            // frappeur à sa base, et la règle `rallye` lit la position
            // ÉVOLUÉE — poser `applyReplacement` en premier FABRIQUERAIT des
            // frappes fantômes. Placés AVANT `enforceRegulation` et AVANT
            // `dropStillMoves`, les deux contraintes qu'énonce analysis.js:301.
            // Mesuré sur 428 scènes IA capturées (raquettes + volley témoin) :
            // `replacement` 14 -> 7, ZÉRO faux positif, zéro défaut de
            // lisibilité ajouté. `applyStrikeApproach` n'y répare aucune frappe
            // fantôme (son garde est conditionné au TEXTE, la règle ne l'est
            // pas) mais délivre sur cette route le « petit déplacement entre
            // chaque frappe » du 👎 PROD103, à coût de garde nul.
            try { applyStrikeApproach(s, sp, text); } catch (_) { /* idem */ }
            try { applyReplacement(s, sp, text); } catch (_) { /* idem */ }
            // COURSES CROISÉES — la seule règle du dépôt dont le message
            // PRESCRIVAIT le geste sans que personne ne l'applique (« ÉCHANGER
            // les points d'arrivée des deux coureurs »). AVANT enforceRegulation
            // et AVANT les correcteurs de tir : l'origine d'un tir dépend de la
            // position ATTEINTE, que la permutation déplace.
            try { enforceCrossedRuns(s, sp, text); } catch (_) { /* idem */ }
            try { enforceRegulation(s, sp, text); } catch (_) { /* idem */ }
            try { enforceShotDepth(s, sp, text); } catch (_) { /* idem */ }
            try { enforceBackwardPass(s, sp, text); } catch (_) { /* idem */ }
            // Le correcteur de `frappe fantôme`, aligné sur le prédicat de sa
            // règle là où `applyStrikeApproach` dépend du TEXTE. APRÈS les
            // correcteurs de position, qui déplacent les retombées : mesuré
            // frappe fantôme 5 -> 1 et +5 cas sur 214 scènes capturées, 0 faux
            // positif, 0 défaut de lisibilité ajouté.
            try { enforceStrikeOrigin(s, sp, text); } catch (_) { /* idem */ }
            // …et les ARRIVÉES de tir conduites jusqu'à la cible : le TIR est
            // la 1re cause de rejet du dépôt et n'avait aucun correcteur.
            try { enforceShotOrigin(s, sp, text); } catch (_) { /* idem */ }
            try { enforceShotTarget(s, sp, text); } catch (_) { /* idem */ }
            // Les flèches de déplacement qui ne vont NULLE PART : le juge les
            // signale (`action_vide`), et `dropStillMoves` — écrit, testé,
            // appelé dans finish() — n'avait jamais été branché de ce côté.
            // Cinquième et dernier exemplaire du motif de la session.
            // …et `renumberOrders` DANS LA FOULÉE, comme dans finish() :
            // supprimer une action TROUE la numérotation, et le rendu affiche
            // l'ordre sur la flèche — l'enseignant lirait « 1, 2, 4 ».
            try { dropStillMoves(s); renumberOrders(s); } catch (_) { /* idem */ }
            try { applyStadeTerrain(s, sp, text); } catch (_) { /* idem */ }
            try { fitAgresToAction(s, sp, text); } catch (_) { /* idem */ }
            // NEUVIÈME EXEMPLAIRE DU MOTIF, et le plus coûteux mesuré. Les CINQ
            // correcteurs d'AJOUT — effectif complété, enseignant posté, matériel
            // nommé, gardien décrit, défense qui bouge — tournent dans
            // `routeDecision` APRÈS l'arbitrage du garde-fou de fidélité, donc au
            // REPLI seulement. C'était délibéré : placés dans `finish()`, ils
            // pesaient sur le CHOIX de l'interprète (analysis.js:371). Mais
            // `projeter()` n'a AUCUN arbitrage à fausser — les deux tirages y
            // reçoivent le même traitement — et la raison de les tenir au repli
            // ne s'y applique donc pas. Ils traitent depuis huit sessions des
            // classes que la garde REJETTE ici, sans jamais y tourner.
            // Ordre et compagnon repris de `routeDecision` : `enforceRegulation`
            // rejoué derrière, qui sort les renforts de la surface de but et les
            // désempile (sans lui, 3 scènes se dégradaient, toutes en
            // `zone_gardien`).
            // Mesuré sur 650 scènes IA capturées : acceptées 299 -> 311/325,
            // rejets 26 -> 14, `defense_immobile` 7 -> 2, les cinq classes
            // d'effectif (« N contre N », « zone 2-3 », « bloc médian », « N
            // attaquants ») quasi soldées, AUCUNE classe neuve, et lisibilité
            // IDENTIQUE AU BIT PRÈS alors qu'ils ajoutent des pions.
            try {
              completeRoster(s, sp, text);
              addPostedTeacher(s, sp, text);
              addNamedGear(s, sp, text);
              addDescribedKeeper(s, sp, text);
              addDefenderMove(s, sp, text);
              enforceRegulation(s, sp, text);
            } catch (_) { /* idem */ }
            return s;
          };
          const pick = pickBestScene([projeter(dS?.data), projeter(dS?.data_alt)], text, sportHint);
          let scene = pick ? pick.scene : dS?.data;
          if (pick) {
            const sportScene = sportOverride || textSport || scene.sport;
            let gate = pick.gate;
            // POLITIQUE DE RÉPARATION (retours prod 👎 « beaucoup trop long ») :
            // - défaut GRAVE -> réparation EN PREMIER PLAN (elle peut faire
            //   basculer rejet -> accepté, l'attente est le prix d'un schéma
            //   juste) ;
            // - défauts SOFT seulement -> la scène est DÉJÀ acceptée : servie
            //   TOUT DE SUITE, le polissage part en ARRIÈRE-PLAN après le rendu
            //   (mesure salve nº1 : 14 réparations sur 17 étaient soft-only —
            //   ~36 s d'attente évitée sur l'écrasante majorité des cas).
            if (repairMode(gate) === "foreground") {
              loaderText("Contrôle qualité — l'IA corrige…");
              genStep(2, "Contrôle qualité — l'IA corrige la scène…");
              try {
                const { data: dR } = await supabase.functions.invoke("generate", {
                  // `generation_id` rattache la correction à la génération qui
                  // l'a payée : le serveur l'offre (une fois), après avoir
                  // vérifié en base que cette ligne appartient bien au compte.
                  body: { prompt: `${promptContext()}\n\n${text}`, sportOverride, mode: "scene",
                    generation_id: rootGenId,
                    repair: { previous: scene, issues: [...gate.grave, ...gate.soft] } },
                });
                if (dR?.usage) updateQuota(dR.usage.used, dR.usage.quota);
                const rep = dR?.data;
                if (isValidSceneResult(rep)) {
                  projeter(rep);          // la garde juge la scène DESSINÉE, réparation comprise
                  const g2 = sceneGate(rep, text, sportOverride || textSport || rep.sport);
                  // Gardée seulement si STRICTEMENT meilleure (même règle que
                  // la retente ciblée du pipeline classique).
                  if (repairIsBetter(gate, g2)) { scene = rep; gate = g2; }
                }
              } catch (e) { console.warn("Réparation scène indisponible :", e); }
            }
            if (gate.accept) {
              forceSport(scene);
              // Marqueur d'AFFICHAGE demi-terrain (meta seulement, coordonnées
              // intactes) — posé APRÈS la garde : les juges ont vu la scène
              // exactement comme le pipeline classique la verrait.
              applyHalfCourt(scene, text, scene.sport);
              state.lastAiResult = { prompt: text, sport: scene.sport, kind: scene.kind || null,
                analyse: null, title: scene.title || null, pedagogy: scene.pedagogy || null, sceneMode: true };
              refreshDevTools();
              applyMeta(scene);
              state.currentSchemaId = null;
              logRoute("scene", { prompt: text, template: recognizedTemplate(text, parseOpts), sport: scene.sport, kind: scene.kind });
              finishRender(scene, "scene");
              // POLISSAGE EN ARRIÈRE-PLAN : la scène acceptée est affichée, la
              // réparation de lisibilité court sans bloquer personne.
              if (repairMode(gate) === "background")
                launchScenePolish({ scene, gate, text, sportOverride, sportScene, rootGenId });
              return;
            }
            // Scène refusée par la garde : trace pour l'arbitrage, puis repli.
            loaderText("Schéma non conforme — pipeline classique…");
            genStep(2, "Schéma non conforme — nouvelle interprétation…");
            logRoute("scene_rejetee", { prompt: text, template: recognizedTemplate(text, parseOpts), sport: sportScene, kind: state.kind });
          }
        }
      } catch (e) {
        console.warn("Mode scène indisponible, pipeline classique :", e);
        // Troisième chemin muet : l'échec technique ne partait que dans la
        // console du navigateur. Il compte pourtant comme une génération que
        // la nouvelle architecture n'a pas servie.
        logRoute("scene_erreur", { prompt: text, sport: sportOverride || textSport || null, kind: state.kind });
      }
    }

    const { data, error } = await supabase.functions.invoke("generate", {
      // Si le mode scène a déjà payé un crédit, ce passage au pipeline classique
      // est un REPLI interne, pas une nouvelle demande : il cite la racine et le
      // serveur l'offre (`fallback` sert à l'étiqueter dans le journal).
      body: { prompt: `${promptContext()}\n\n${text}`, sportOverride,
              ...(rootGenId ? { generation_id: rootGenId, fallback: true } : {}) },
    });

    if (error) {
      const detail = await readFunctionError(error);
      if (detail?.error === "quota_exceeded") {
        showError(quotaExceededMessage(detail.quota));
        updateQuota(detail.quota, detail.quota);
        return;
      }
      console.warn("Edge Function en échec, repli local :", detail ? JSON.stringify(detail) : "(pas de détail)", error);
      showError("Service IA indisponible — repli sur l'analyse locale (résultat simplifié).");
      logRoute("fallback", { prompt: text, template: recognizedTemplate(text, parseOpts), sport: sportOverride || textSport || null, kind: state.kind });
      // Via routeDecision (sans analyse IA -> toujours la scène locale) pour que
      // les OVERLAYS génériques (observateur, colonnes d'attente, portes, départ
      // matérialisé…) s'appliquent aussi au repli hors-ligne — sinon le repli
      // rendait MOINS que le même prompt servi par l'IA.
      const fallback = routeDecision({ players: [] }, { text, sport: sportOverride || textSport || undefined, parseOpts }).scene;
      forceSport(fallback);
      adoptLocalAnalysis(fallback, text);
      applyMeta(fallback);
      if (state.analysis) fallback.meta.analyse = state.analysis.analyse;
      state.currentSchemaId = null;
      finishRender(fallback, "fallback");
      return;
    }

    const result = data?.data;
    if (!result || !Array.isArray(result.players)) throw new Error("Réponse invalide du serveur.");
    forceSport(result);
    if (data.generation_id) rootGenId = rootGenId || data.generation_id;
    if (data.usage) updateQuota(data.usage.used, data.usage.quota);
    state.currentSchemaId = null;

    // ARCHITECTURE V2 — « l'IA comprend, le code dessine » : si l'IA fournit
    // une ANALYSE STRUCTURÉE valide (famille filet), le moteur local dessine
    // à partir d'elle (placement fiable, plus d'arbitrage entre deux scènes).
    // Titre et pédagogie de l'IA sont conservés. Analyse invalide -> pipeline
    // actuel ci-dessous, comme avant.
    const sportResolved = sportOverride || textSport || result.sport;
    // DEV (admin) : on retient la réponse IA BRUTE pour pouvoir l'exporter en
    // fixture de test (chemin IA rejouable hors-ligne).
    state.lastAiResult = { prompt: text, sport: sportResolved, kind: result.kind || null, analyse: result.analyse || null, title: result.title || null, pedagogy: result.pedagogy || null };
    refreshDevTools();
    // ARBITRAGE : un seul point de décision (routeDecision, src/analysis.js).
    // Il choisit la scène (analyse IA, template local, repli hybride ou scène
    // brute) ; ici on n'applique que les EFFETS (titre/pédagogie, meta, rendu).
    let chosen = routeDecision(result, { text, sport: sportResolved, parseOpts });
    // RETENTE CIBLÉE (autocorrection) — si la scène routée CONTREDIT encore le
    // texte (le garde-fou interne de routeDecision a déjà essayé l'autre
    // interprète), on repasse UNE fois par l'IA en lui disant exactement quoi
    // corriger. La 2e réponse n'est GARDÉE que si elle fait STRICTEMENT mieux
    // au détecteur de fidélité. Rare (quelques % des générations) ; best-effort
    // (toute erreur laisse la 1re scène).
    try {
      const issues = fidelityIssues(chosen.scene, text, sportResolved);
      if (issues.length) {
        loaderText("Le schéma contredit le texte — l'IA corrige…");
        genStep(2, "Le schéma contredit le texte — l'IA corrige…");
        const { data: d2 } = await supabase.functions.invoke("generate", {
          // `retry: true` étiquette la ligne `…+retry` dans le journal ;
          // `generation_id` rattache la retente à la génération qui l'a payée,
          // ce qui la rend GRATUITE (une fois, vérifié en base côté serveur).
          body: { prompt: correctionPrompt(`${promptContext()}\n\n${text}`, result.analyse, issues), sportOverride,
                  retry: true, generation_id: rootGenId },
        });
        const result2 = d2?.data;
        if (result2 && Array.isArray(result2.players)) {
          const alt = routeDecision(result2, { text, sport: sportResolved, parseOpts });
          if (fidelityIssues(alt.scene, text, sportResolved).length < issues.length) {
            result.analyse = result2.analyse;
            if (result2.title) result.title = result2.title;
            if (result2.pedagogy) result.pedagogy = result2.pedagogy;
            state.lastAiResult = { prompt: text, sport: sportResolved, kind: result2.kind || result.kind || null, analyse: result2.analyse || null, title: result.title || null, pedagogy: result.pedagogy || null };
            chosen = alt;
            state.lastCorrected = true;
            logRoute("autocorrection", { prompt: text, sport: sportResolved, kind: result.kind, famille: result2?.analyse?.famille });
          }
          if (d2.usage) updateQuota(d2.usage.used, d2.usage.quota);
        }
      }
    } catch (e) { console.warn("Retente ciblée indisponible :", e); }
    const { reason, scene } = chosen;
    logRoute(reason, { prompt: text, template: recognizedTemplate(text, parseOpts), sport: sportResolved, kind: result.kind, famille: result?.analyse?.famille });
    if (reason === "analysis") {
      const fromAnalysis = scene;
      fromAnalysis.sport = sportResolved;
      fromAnalysis.kind = result.kind || "exercise";
      if (result.title) fromAnalysis.title = result.title;
      const pedA = result.pedagogy || {};
      if (pedA.objectif || (pedA.organisation || []).length || (pedA.criteres_reussite || []).length || (pedA.erreurs || []).length)
        fromAnalysis.pedagogy = pedA;
      applyMeta(fromAnalysis);
      // L'analyse accompagne la scène : panneau « Ce que j'ai compris » +
      // persistance dans le schéma enregistré (rouverte depuis la bibliothèque).
      fromAnalysis.meta.analyse = result.analyse;
      state.analysis = { analyse: result.analyse, sport: sportResolved, text };
      finishRender(fromAnalysis, "analysis");
      return;
    }
    if (reason === "template" || reason === "hybrid") {
      const local = scene;
      forceSport(local);
      adoptLocalAnalysis(local, text);
      if (result.title) local.title = result.title;
      const ped = result.pedagogy || {};
      if (ped.objectif || (ped.organisation || []).length || (ped.criteres_reussite || []).length || (ped.erreurs || []).length)
        local.pedagogy = ped;
      applyMeta(local);
      if (state.analysis) local.meta.analyse = state.analysis.analyse;
      finishRender(local, reason);
      return;
    }
    applyMeta(result);
    finishRender(result, "edge");
  } catch (err) {
    console.error(err);
    showError("Erreur : " + (err?.message || err));
  } finally {
    setLoading(false);
  }
}

/* TÉLÉMÉTRIE DE ROUTE (préparation « étape 2 ») — enregistre la décision de
   routage de chaque GÉNÉRATION réelle (jamais l'aperçu immédiat, jamais les
   redessins locaux). Écriture seule et fire-and-forget : aucun await bloquant,
   tout échec (hors-ligne, RLS, table absente) est silencieux — la télémétrie
   ne peut pas dégrader l'expérience. */
function logRoute(reason, { prompt, template, sport, kind, famille } = {}) {
  try {
    supabase.from("route_stats").insert({
      reason,
      template: template || null,
      sport: sport || null,
      kind: kind || null,
      famille: famille || null,
      prompt: String(prompt || "").slice(0, 2000),
    }).then(() => {}, () => {});
  } catch (_) { /* silencieux : l'observation n'interfère jamais */ }
}

async function readFunctionError(error) {
  try {
    if (error?.context && typeof error.context.json === "function") return await error.context.json();
  } catch (_) { /* ignore */ }
  return null;
}

const EMPTY_SCENE = () => ({ kind: "exercise", sport: state.activeSport, players: [], actions: [], zones: [], markers: [] });

/* Mode SIMPLE : vue allégée NON destructive (lastJson reste la source).
   Les joueurs NON impliqués dans l'action restent VISIBLES mais ESTOMPÉS — un
   4-4-2 reste un 4-4-2 — et les actions secondaires (sans chronologie) sont
   atténuées. Le mode Détaillé montre tout à pleine intensité. */
function viewScene(data) {
  if (!data || !state.simpleMode) return { view: data, mutedPlayers: null };
  const players = Array.isArray(data.players) ? data.players : [];
  const actions = Array.isArray(data.actions) ? data.actions : [];
  const muteSecondary = (a) => (a.order == null ? { ...a, muted: true } : a);
  const view = { ...data, actions: actions.map(muteSecondary) };
  if (!actions.length || players.length <= 6) return { view, mutedPlayers: null };
  const ids = new Set();
  actions.forEach((a) => { if (a.from) ids.add(a.from); if (a.to) ids.add(a.to); });
  const muted = new Set(players.filter((p) => !ids.has(p.id) && p.team !== "OBS" && p.team !== "k").map((p) => p.id));
  return { view, mutedPlayers: muted.size ? muted : null };
}
function refreshMeta() {
  const d = state.lastJson;
  const pill = $("kindPill");
  // Avant toute génération, le pill suit le mode par DÉFAUT (segmenté) ;
  // ensuite, il reflète la scène générée.
  const isEx = d ? d.kind === "exercise" : state.kind === "exercise";
  pill.textContent = isEx ? "Exercice" : "Match";
  pill.className = "kind-pill " + (isEx ? "kind-exercise" : "kind-match");
  $("schemaTitle").textContent = d?.title || "Schéma tactique";
  // Ligne de stats sous le canvas : retirée du gabarit épuré, donc tolérée absente.
  const setMeta = (id, val) => { const el = $(id); if (el) el.textContent = val; };
  setMeta("activeSport", sportLabel(state.activeSport));
  setMeta("playerCount", (d?.players || []).length);
  setMeta("actionCount", (d?.actions || []).length);
  setMeta("zoneCount", (d?.zones || []).length);
}
/* Repeint la scène courante (vue dérivée) SANS re-sanitiser — utilisé par les
   bascules d'affichage et l'édition manuelle. */
function paint() {
  if (!state.lastJson) { state.sceneView = null; renderScene(EMPTY_SCENE(), state.activeSport, state.renderMode); return; }
  const { view, mutedPlayers } = viewScene(state.lastJson);
  state.sceneView = view;
  const selection = state.editing && state.editor ? state.editor.getSelection() : null;
  renderScene(view, state.activeSport, state.renderMode, { selection, mutedPlayers });
}
/* ----------------------------------------------------------------------------
   ÉCRAN D'ATTENTE multi-sports : tant qu'aucun schéma n'est généré, on fait
   défiler des terrains variés (familles invasion / filet / parcours / athlé)
   pour montrer d'emblée que TactiQ ne se limite pas au football.
   -------------------------------------------------------------------------- */
const IDLE_SPORTS = ["football", "basketball", "handball", "volleyball", "badminton", "rugby", "gymnastique", "athletisme"];
let idleTimer = null, idleIdx = 0;
function drawIdle(sport) {
  renderScene({ kind: "exercise", sport, players: [], actions: [], zones: [], markers: [] }, sport, state.renderMode);
  const lbl = $("idleSport"); if (lbl) lbl.textContent = sportLabel(sport);
}
function idleTick() {
  if (state.lastJson || state.view !== "editor") return;   // ne tourne que sur l'éditeur vierge
  idleIdx = (idleIdx + 1) % IDLE_SPORTS.length;
  drawIdle(IDLE_SPORTS[idleIdx]);
}
function startIdle() {
  if (state.lastJson) return;
  drawIdle(IDLE_SPORTS[idleIdx]);
  if (!idleTimer) idleTimer = setInterval(idleTick, 2600);
}
function stopIdle() {
  if (idleTimer) { clearInterval(idleTimer); idleTimer = null; }
}

/* Bannière bêta 👍/👎 : visible sous un schéma issu d'une GÉNÉRATION (pas un
   import, pas une création manuelle, pas l'aperçu template-first qui sera
   écrasé par le rendu final). Ré-armée à chaque nouveau schéma. */
const FEEDBACK_SOURCES = new Set(["analysis", "template", "hybrid", "edge", "fallback", "scene"]);
function resetFeedbackBar(source) {
  const bar = $("feedbackBar");
  if (!bar) return;
  bar.hidden = !FEEDBACK_SOURCES.has(source);
  for (const id of ["fbComment", "fbSend", "fbThanks"]) { const el = $(id); if (el) el.hidden = true; }
  const c = $("fbComment"); if (c) c.value = "";
  for (const id of ["fbUp", "fbDown"]) { const b = $(id); if (b) { b.disabled = false; b.removeAttribute("data-picked"); } }
}
function sendFeedback(verdict) {
  try {
    const prompt = state.lastText || "";
    const sport = state.lastAiResult?.sport || state.activeSport || null;
    let issues = [];
    try { issues = fidelityIssues(state.lastJson, prompt, sport); } catch (_) { /* best-effort */ }
    const rec = buildFeedbackRecord({
      verdict,
      comment: $("fbComment")?.value,
      prompt,
      sport,
      kind: state.lastAiResult?.kind || state.lastJson?.kind || null,
      route: state.source || null,
      corrected: !!state.lastCorrected,
      analyse: state.lastAiResult?.analyse || state.analysis?.analyse || null,
      scene: state.lastJson,
      issues,
    });
    if (!rec) return;
    supabase.from("feedback").insert(rec).then(() => {}, () => {});
  } catch (_) { /* silencieux : le retour n'interfère jamais avec l'app */ }
  for (const id of ["fbComment", "fbSend"]) { const el = $(id); if (el) el.hidden = true; }
  for (const id of ["fbUp", "fbDown"]) { const b = $(id); if (b) b.disabled = true; }
  const t = $("fbThanks"); if (t) t.hidden = false;
}
function initFeedbackBar() {
  const up = $("fbUp"), down = $("fbDown"), send = $("fbSend");
  if (!up || !down) return;
  up.addEventListener("click", () => { up.setAttribute("data-picked", "1"); sendFeedback("up"); });
  down.addEventListener("click", () => {
    // Le 👎 ouvre le commentaire optionnel — l'envoi se fait au bouton (ou
    // directement si l'utilisateur re-clique sans rien écrire).
    down.setAttribute("data-picked", "1");
    const c = $("fbComment"); if (c) { c.hidden = false; c.focus(); }
    if (send) send.hidden = false;
  });
  if (send) send.addEventListener("click", () => sendFeedback("down"));
  const c = $("fbComment");
  if (c) c.addEventListener("keydown", (e) => { if (e.key === "Enter") sendFeedback("down"); });
}

function finishRender(data, source) {
  sanitizeScene(data);
  stopIdle();   // un schéma est désormais affiché : fin de l'écran d'attente
  if (state.editor) state.editor.clearSelection();   // la sélection pointait l'ancienne scène
  closeFicheEditSilently();   // le formulaire de fiche portait sur l'ancienne scène
  state.lastJson = data;
  state.source = source;
  resetFeedbackBar(source);
  state.activeSport = resolveSport(data.sport);
  renderPedagogy(data);
  refreshMeta();
  updateArrowTypeOptions();
  showUnderstanding(state.analysis);
  history.reset(data);      // nouvelle scène = nouvelle baseline d'annulation
  updateUndoUi();
  paint();
}

/* Adopte l'analyse SYNTHÉTISÉE par le moteur local (repli / template-first) :
   le panneau « Ce que j'ai compris » est disponible même sans analyse IA —
   notamment hors-ligne ou tant que l'Edge Function n'est pas redéployée. */
function adoptLocalAnalysis(scene, text) {
  state.analysis = scene?.analyse ? { analyse: scene.analyse, sport: scene.sport, text } : null;
  if (scene?.analyse) delete scene.analyse;
}

/* « Ce que j'ai compris » -> Redessiner : re-rendu LOCAL depuis l'analyse
   corrigée (aucun appel IA, aucun quota). Titre et pédagogie sont conservés. */
function applyUnderstanding(analyse) {
  const ctx = state.analysis;
  if (!ctx) return false;
  const scene = sceneFromAnalysis(analyse, ctx.sport, ctx.text || "");
  if (!scene) return false;
  ctx.analyse = analyse;
  scene.sport = ctx.sport;
  scene.kind = state.lastJson?.kind === "match" ? "match" : "exercise";
  if (state.lastJson?.title) scene.title = state.lastJson.title;
  if (state.lastJson?.pedagogy) scene.pedagogy = state.lastJson.pedagogy;
  resetEditorChrome();
  applyMeta(scene, ctx.text);
  scene.meta.analyse = analyse;
  finishRender(scene, "analysis-edit");
  return true;
}

/* Le menu « Type de flèche » de l'éditeur s'adapte à la FAMILLE du sport actif :
   - filet / raquette : pas de conduite, de centre ni d'écran ; la passe devient
     « Service / renvoi » (type EPS `renvoi`) ;
   - invasion : jeu complet (passe, course, conduite, tir, centre, écran) ;
   - autres activités (gym, athlé, tir à l'arc, pétanque…) : set générique.
   Recalculé à chaque génération, remise à zéro ou changement de sport, pour que
   les options collent toujours au terrain affiché. */
const RACKET_SPORTS = new Set(["badminton", "tennis", "tennis_de_table", "volleyball"]);
const INVASION_SPORTS = new Set(["football", "futsal", "basketball", "basket3x3", "handball", "rugby", "ultimate"]);
function arrowTypesForSport(sport) {
  if (RACKET_SPORTS.has(sport)) return [
    ["renvoi", "Service / renvoi"],
    ["course", "Déplacement / course"],
    ["tir", "Frappe / smash"],
  ];
  if (INVASION_SPORTS.has(sport)) return [
    ["passe", "Passe"],
    ["course", "Déplacement / course"],
    ["dribble", "Conduite / dribble"],
    ["tir", "Tir / frappe"],
    ["centre", "Centre"],
    ["ecran", "Écran / bloc"],
  ];
  return [
    ["passe", "Passe / transmission"],
    ["course", "Déplacement / course"],
    ["tir", "Lancer / tir"],
  ];
}
function updateArrowTypeOptions() {
  const sel = $("arrowType");
  if (!sel) return;
  const prev = sel.value;
  const types = arrowTypesForSport(state.activeSport);
  sel.innerHTML = types.map(([v, l]) => `<option value="${v}">${l}</option>`).join("");
  // On conserve le type courant s'il reste pertinent, sinon le 1er de la liste.
  sel.value = types.some(([v]) => v === prev) ? prev : types[0][0];
  // Aligne le type par défaut de l'éditeur (sans toucher à une flèche sélectionnée).
  if (state.editor && !state.editor.getSelection()) state.editor.setActionType(sel.value);
  // Même déclencheur pour la bibliothèque : sa catégorie « sport actif »
  // remonte en tête à chaque changement de sport / génération.
  buildElementLibrary();
}

/* ============================================================================
   4) PANNEAU PÉDAGOGIQUE
   ========================================================================== */
/* Échappe aussi les guillemets : les valeurs sont injectées dans des
   attributs (value="…" du formulaire de fiche) comme dans du texte. */
function esc(s) { return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])); }
const ACTION_TYPE_LABEL = { passe: "Passe", renvoi: "Renvoi / échange", course: "Déplacement / course", tir: "Tir / frappe", dribble: "Conduite / dribble", centre: "Centre", ecran: "Écran" };
/* Couleurs par type d'action — IDENTIQUES à la palette du moteur de rendu,
   pour que les hexagones du Déroulement répondent à ceux du schéma. */
const ACTION_COLORS_UI = { course: "#D95F02", passe: "#0072B2", renvoi: "#0072B2", centre: "#E6AB02", dribble: "#1B9E77", tir: "#7B5CD6", ecran: "#E7298A" };
/* Étapes du déroulement, alignées sur les hexagones du schéma. On dérive en
   PRIORITÉ des actions ORDONNÉES (numéro + type -> couleur de l'hexagone +
   libellé) ; `data.steps` ne sert que de repli quand aucune action n'est
   numérotée (ex. parcours gym). */
function deroulementItems(data) {
  const ordered = (data?.actions || []).filter((a) => a.order != null).slice().sort((x, y) => x.order - y.order);
  if (ordered.length) return ordered.map((a) => ({ n: a.order, text: a.label || ACTION_TYPE_LABEL[a.type] || "Action", type: a.type }));
  if (Array.isArray(data?.steps) && data.steps.length) return data.steps.map((s, i) => ({ n: i + 1, text: String(s), type: "course" }));
  return [];
}
function deroulementSteps(data) { return deroulementItems(data).map((it) => it.text); }
/* Hexagone numéroté (SVG inline) identique aux repères du schéma. */
function hexStepHtml(it) {
  const c = ACTION_COLORS_UI[it.type] || "#0072B2";
  return `<li><svg class="hexnum" viewBox="0 0 24 24" aria-hidden="true">` +
    `<polygon points="20.7,17 12,22 3.3,17 3.3,7 12,2 20.7,7" fill="#fff" stroke="${c}" stroke-width="2"/>` +
    `<text x="12" y="13" text-anchor="middle" dominant-baseline="middle" fill="${c}" font-size="10.5" font-weight="700">${esc(String(it.n))}</text>` +
    `</svg><span>${esc(it.text)}</span></li>`;
}
function pedagogyHtml(data) {
  const ped = data?.pedagogy || {};
  const section = (t, inner) => `<div class="peda-section"><h3>${t}</h3>${inner}</div>`;
  const list = (items, cls) => `<ul class="peda-list ${cls}">${items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>`;
  let html = "";
  if (data.title) html += `<p class="peda-title">${esc(data.title)}</p>`;
  if (ped.objectif) html += section("🎯 Objectif", `<div class="objectif">${esc(ped.objectif)}</div>`);
  if ((ped.organisation || []).length) html += section("🛠️ Organisation", list(ped.organisation, ""));
  const items = deroulementItems(data);
  if (items.length >= 2) html += section("🧭 Déroulement", `<ul class="peda-steps hexsteps">${items.map(hexStepHtml).join("")}</ul>`);
  if ((ped.criteres_reussite || []).length) html += section("✅ Critères de réussite", list(ped.criteres_reussite, "criteres"));
  if ((ped.erreurs || []).length) html += section("⚠️ Erreurs à éviter", list(ped.erreurs, "erreurs"));
  return html;
}
function renderPedagogy(data) {
  const box = $("pedaContent");
  const ped = data?.pedagogy || {};
  // La fiche s'affiche dès qu'un champ pédagogique existe — y compris sur un
  // schéma de MATCH (l'utilisateur peut désormais rédiger la sienne).
  const hasPeda = !!(ped.objectif || (ped.organisation || []).length || (ped.criteres_reussite || []).length || (ped.erreurs || []).length);
  const hasContent = hasPeda || deroulementSteps(data).length >= 2;
  state.hasPeda = !!hasContent;
  $("pedaEditBtn").hidden = !state.lastJson;
  // Formulaire d'édition ouvert : on ne reconstruit PAS le contenu (la saisie
  // en cours serait perdue) — la vue se resynchronisera à la fermeture.
  if (!state.pedaEditing) {
    box.innerHTML = hasContent ? pedagogyHtml(data)
      : `<p class="peda-empty">Aucune fiche pour l'instant : clique <strong>✏️ Modifier</strong> ci-dessus pour rédiger objectif, organisation, critères et erreurs — ou génère une situation.</p>`;
  }
  updateRightPanel();
}

/* ----------------------------------------------------------------------------
   ÉDITION DE LA FICHE PÉDAGOGIQUE — titre, objectif, organisation, critères,
   erreurs, modifiables en place (le Déroulement, lui, suit les flèches
   numérotées du schéma). Chaque saisie est appliquée à la scène et versée
   dans l'historique annuler/rétablir (coalescence des frappes).
   -------------------------------------------------------------------------- */
const FICHE_LISTS = [
  { key: "organisation", label: "🛠️ Organisation", ph: "Ex : 2 attaquants, 1 défenseur, demi-terrain" },
  { key: "criteres_reussite", label: "✅ Critères de réussite", ph: "Ex : 3 tirs cadrés sur 5" },
  { key: "erreurs", label: "⚠️ Erreurs à éviter", ph: "Ex : partir avant la passe" },
];
function fichePed() {
  const d = state.lastJson;
  d.pedagogy = d.pedagogy || {};
  FICHE_LISTS.forEach(({ key }) => { d.pedagogy[key] = d.pedagogy[key] || []; });
  return d.pedagogy;
}
function onFicheChanged() {
  refreshMeta();                       // le titre suit la saisie
  history.record(state.lastJson);
  updateUndoUi();
}
function renderFicheForm() {
  const d = state.lastJson, ped = fichePed();
  const row = (key, val, i) =>
    `<div class="pf-row"><input data-list="${key}" data-i="${i}" value="${esc(val)}">` +
    `<button type="button" class="pf-del" data-list="${key}" data-i="${i}" title="Retirer cette ligne">✖</button></div>`;
  $("pedaContent").innerHTML = `<div class="pf">
    <label class="pf-field">Titre du schéma
      <input id="pfTitle" maxlength="80" value="${esc(d.title || "")}" placeholder="Ex : Contre-attaque 2 contre 1">
    </label>
    <label class="pf-field">🎯 Objectif
      <textarea id="pfObjectif" rows="3" placeholder="Ce que les élèves doivent apprendre / réussir">${esc(ped.objectif || "")}</textarea>
    </label>
    ${FICHE_LISTS.map(({ key, label, ph }) => `<div class="pf-field">
      <span class="pf-label">${label}</span>
      ${ped[key].map((v, i) => row(key, v, i)).join("")}
      <button type="button" class="pf-add" data-list="${key}" data-ph="${esc(ph)}">＋ Ajouter une ligne</button>
    </div>`).join("")}
    <p class="pf-note">🧭 Le <strong>Déroulement</strong> se construit tout seul à partir des flèches numérotées du schéma.</p>
  </div>`;
}
function enterFicheEdit() {
  if (!state.lastJson) return;
  state.pedaEditing = true;
  state.rightTab = "peda";
  $("pedaEditBtn").textContent = "✓ Terminer";
  renderFicheForm();
  updateRightPanel();
}
function exitFicheEdit() {
  if (!state.pedaEditing) return;
  state.pedaEditing = false;
  $("pedaEditBtn").textContent = "✏️ Modifier";
  // Nettoyage : lignes vides retirées, champs vides supprimés de la scène.
  const d = state.lastJson;
  if (d?.pedagogy) {
    const ped = d.pedagogy;
    FICHE_LISTS.forEach(({ key }) => {
      ped[key] = (ped[key] || []).map((v) => String(v).trim()).filter(Boolean);
      if (!ped[key].length) delete ped[key];
    });
    if (!String(ped.objectif || "").trim()) delete ped.objectif; else ped.objectif = String(ped.objectif).trim();
    if (!Object.keys(ped).length) delete d.pedagogy;
    history.record(d);
    updateUndoUi();
  }
  renderPedagogy(d || {});
}
function toggleFicheEdit() { state.pedaEditing ? exitFicheEdit() : enterFicheEdit(); }
/* Fermeture SANS resynchronisation (changement de scène : le renderPedagogy
   qui suit s'en charge). */
function closeFicheEditSilently() {
  state.pedaEditing = false;
  $("pedaEditBtn").textContent = "✏️ Modifier";
}
/* Saisies et boutons du formulaire (délégation : le formulaire est reconstruit
   à chaque ajout/retrait de ligne). */
function onFicheInput(e) {
  if (!state.pedaEditing || !state.lastJson) return;
  const t = e.target;
  if (t.id === "pfTitle") { state.lastJson.title = t.value; onFicheChanged(); return; }
  if (t.id === "pfObjectif") { fichePed().objectif = t.value; onFicheChanged(); return; }
  if (t.dataset.list && t.tagName === "INPUT") {
    fichePed()[t.dataset.list][+t.dataset.i] = t.value;
    onFicheChanged();
  }
}
function onFicheClick(e) {
  if (!state.pedaEditing || !state.lastJson) return;
  const add = e.target.closest(".pf-add");
  if (add) {
    fichePed()[add.dataset.list].push("");
    renderFicheForm();
    const inputs = $("pedaContent").querySelectorAll(`input[data-list="${add.dataset.list}"]`);
    const last = inputs[inputs.length - 1];
    if (last) { last.placeholder = add.dataset.ph || ""; last.focus(); }
    onFicheChanged();
    return;
  }
  const del = e.target.closest(".pf-del");
  if (del) {
    fichePed()[del.dataset.list].splice(+del.dataset.i, 1);
    renderFicheForm();
    onFicheChanged();
  }
}

/* ----------------------------------------------------------------------------
   COLONNE DE DROITE — onglets Fiche / Édition.
   Hors mode édition (ou sur écran étroit) : comportement historique, la fiche
   s'affiche si elle a du contenu, sinon la colonne disparaît. En mode édition
   sur grand écran : les onglets apparaissent et l'inspecteur occupe l'onglet
   « Édition » (bascule automatique à l'ouverture/fermeture du mode ✏️).
   -------------------------------------------------------------------------- */
function updateRightPanel() {
  const inspAvail = state.editing && deskMq.matches;
  if (!inspAvail && state.rightTab !== "peda") state.rightTab = "peda";
  const showInsp = inspAvail && state.rightTab === "edit";
  // Dès qu'une scène existe, le panneau Fiche reste accessible (contenu réel
  // ou invite « ✏️ Modifier » pour la rédiger) — il ne disparaît qu'avant
  // toute génération/création.
  const showPeda = !showInsp && (state.hasPeda || inspAvail || state.pedaEditing || !!state.lastJson);
  $("rightTabs").hidden = !inspAvail;
  $("inspectorPanel").hidden = !showInsp;
  $("pedaPanel").hidden = !showPeda;
  $("rightPanel").hidden = !(showInsp || showPeda);
  if (inspAvail) setSegActive("rightTabs", "data-rtab", state.rightTab);
}

/* Les blocs d'édition (propriétés de sélection #selProps, bibliothèque
   d'éléments #elemLibWrap) vivent dans l'inspecteur sur grand écran, et
   REVIENNENT sous la palette sur écran étroit — même DOM, mêmes ids, seuls
   le parent et la mise en forme changent. */
function relocateEditorBlocks() {
  const desk = deskMq.matches;
  const props = $("selProps");
  const propsTarget = desk ? $("inspSlot") : $("paletteSelSlot");
  if (props.parentElement !== propsTarget) propsTarget.appendChild(props);
  const lib = $("elemLibWrap");
  const libTarget = desk ? $("inspectorPanel").querySelector(".insp-body") : $("paletteLibSlot");
  if (lib.parentElement !== libTarget) libTarget.appendChild(lib);
  lib.open = desk;   // déplié dans la colonne, replié (gain de place) dans la palette
  updateRightPanel();
}

/* Construit la bibliothèque d'éléments depuis le catalogue (editor.js) :
   chaque bouton est un outil de placement, au même titre que la palette.
   - la CATÉGORIE DU SPORT ACTIF remonte en tête (marquée ★) ;
   - chaque bouton porte une VIGNETTE dessinée par le moteur de rendu
     (renderElementIcon) : fidèle au rendu réel sur le terrain. */
const SPORT_LIB_CATEGORY = { gymnastique: "Gymnastique", athletisme: "Athlétisme", petanque: "Pétanque", tir_a_l_arc: "Buts & cibles" };
const LIB_BY_TOOL = {};
ELEMENT_LIBRARY.forEach((c) => c.items.forEach((it) => { LIB_BY_TOOL[it.tool] = it; }));
const libButtonLabel = (label) => label.replace(/^[^\p{L}\p{N}]+\s*/u, "");   // la vignette remplace l'emoji
function buildElementLibrary() {
  const box = $("elemLib");
  if (!box) return;
  const fav = SPORT_LIB_CATEGORY[state.activeSport];
  const cats = fav ? [...ELEMENT_LIBRARY].sort((a, b) => (b.cat === fav) - (a.cat === fav)) : ELEMENT_LIBRARY;
  box.innerHTML = cats.map((c) =>
    `<span class="el-cat">${esc(c.cat)}${c.cat === fav ? " ★" : ""}</span><div class="el-grid">` +
    c.items.map((it) =>
      `<button type="button" data-tool="${esc(it.tool)}" title="Clique puis pose l'élément sur le terrain">` +
      `<canvas class="el-ico" width="108" height="72" aria-hidden="true"></canvas><span>${esc(libButtonLabel(it.label))}</span></button>`).join("") +
    `</div>`).join("");
  box.querySelectorAll("[data-tool]").forEach((b) => {
    const t = b.getAttribute("data-tool");
    try { renderElementIcon(b.querySelector("canvas"), LIB_BY_TOOL[t]); } catch (_) { /* vignette non bloquante */ }
    b.addEventListener("click", () => { state.editor.setTool(t); setActiveTool(t); });
  });
  if (state.editor) setActiveTool(state.editor.getTool());
}

/* ============================================================================
   5) BARRE D'OUTILS : mode de rendu, zoom, impression, export, enregistrement
   ========================================================================== */
function setMode(mode) {
  state.renderMode = mode === "line" ? "line" : "color";
  setSegActive("segMode", "data-mode", state.renderMode);
  setRenderMode(state.renderMode);
  paint();
}
function setSimple(on) {
  state.simpleMode = !!on;
  setSegActive("segSimple", "data-simple", on ? "simple" : "detail");
  paint();
}

/* ----------------------------------------------------------------------------
   ÉDITION MANUELLE (module editor.js) — modèle « sélection d'abord ».
   La palette n'est visible qu'en mode « Éditer » (qui force la vue Détaillé).
   -------------------------------------------------------------------------- */
const EDIT_HINT_DEFAULT = "✋ Glisse un joueur ou la pointe d'une flèche. Active ✏️ pour l'éditeur complet (ajouter, modifier, supprimer).";
function setEditHint(msg) { $("editHint").textContent = msg || EDIT_HINT_DEFAULT; }
function setActiveTool(tool) {
  document.querySelectorAll("#editPalette [data-tool], #elemLib [data-tool]")
    .forEach((b) => b.classList.toggle("is-active", b.getAttribute("data-tool") === tool));
  // Couleur du cône : visible quand on s'apprête à en poser un.
  if (tool === "cone") { $("coneColorWrap").hidden = false; $("coneColor").value = state.editor.getConeColor(); }
  else if (!(state.editor && isConeMarker(state.editor.getSelection()?.ref))) $("coneColorWrap").hidden = true;
}
function playerDisplayNum(p) {
  if (p.num != null) return String(p.num);
  const m = String(p.id || "").match(/\d+/);
  return m ? m[0] : "";
}
/* Libellé humain de l'élément sélectionné (en-tête de l'inspecteur). */
function selKindLabel(sel) {
  if (!sel) return "";
  if (sel.kind === "player") {
    if (sel.ref.team === "OBS") return "👤 " + (sel.ref.role || "Observateur");
    return { A: "🔵 Joueur (équipe A)", B: "🟠 Adversaire (équipe B)", k: "🟡 Gardien" }[sel.ref.team] || "Joueur";
  }
  if (sel.kind === "marker") {
    if (isConeMarker(sel.ref)) return "🔺 Cône";
    const t = String(sel.ref.type || "").toLowerCase();
    if (["ballon", "ball", "balle"].includes(t)) return "⚪ Ballon";
    if (["cible", "target"].includes(t)) return "🎯 Cible";
    return libraryLabel(sel.ref) || "Repère (" + (sel.ref.type || "marqueur") + ")";
  }
  if (sel.kind === "zone") return "▭ Zone" + (sel.ref.label ? " — " + sel.ref.label : "");
  return "➟ Flèche — " + (sel.ref.label || sel.ref.type || "action");
}
function onEditorSelection(sel) {
  $("deleteSelected").disabled = !sel;
  $("selProps").hidden = !sel;
  $("inspEmpty").hidden = !!sel;
  $("selKind").textContent = selKindLabel(sel);
  const isPlayer = !!sel && sel.kind === "player";
  const isAction = !!sel && sel.kind === "action";
  const isZone = !!sel && sel.kind === "zone";
  const isCone = !!sel && sel.kind === "marker" && isConeMarker(sel.ref);
  const isStation = !!sel && sel.kind === "marker" && isStationMarker(sel.ref);
  const isNote = !!sel && sel.kind === "marker" && hasEditableText(sel.ref);
  // Champ N° contextuel : pastille (joueur), étape (flèche) ou n° de station.
  const isPerson = isPlayer && sel.ref.team === "OBS";
  $("selNumWrap").hidden = !(isPlayer || isAction || isStation);
  $("selRoleWrap").hidden = !isPlayer;
  // Couleur de pastille : réservée aux personnes (observateur, arbitre, coach).
  $("selPlayerColorWrap").hidden = !isPerson;
  if (isPerson) $("selPlayerColor").value = sel.ref.color || "#F2C200";
  // Texte : contenu d'une bulle, ou étiquette d'une zone.
  $("selTextWrap").hidden = !(isNote || isZone);
  $("selZoneStyleWrap").hidden = !isZone;
  $("selZoneColorWrap").hidden = !isZone;
  // Duplication : joueurs, éléments et zones (pas les flèches).
  $("duplicateSelected").hidden = !sel || isAction;
  // Couleur : visible pour un cône sélectionné (sinon laissée à l'outil cône).
  if (isCone) { $("coneColorWrap").hidden = false; $("coneColor").value = sel.ref.color || state.editor.getConeColor(); }
  else if (state.editor.getTool() !== "cone") $("coneColorWrap").hidden = true;
  if (isPlayer) { $("selNum").value = playerDisplayNum(sel.ref); $("selRole").value = sel.ref.role || ""; }
  if (isStation) $("selNum").value = sel.ref.n != null ? String(sel.ref.n) : "";
  if (isNote) {
    // Longueur adaptée : bulle courte (rendu 22 c.), cote très courte, texte libre plus ample.
    const t = String(sel.ref.type || "").toLowerCase();
    $("selText").maxLength = t === "texte" ? 40 : t === "cote" ? 12 : 22;
    $("selText").value = sel.ref.text || "";
  }
  if (isZone) {
    $("selText").maxLength = 40;
    $("selText").value = sel.ref.label || "";
    $("selZoneStyle").value = sel.ref.fill || sel.ref.style || "dashed";
    $("selZoneColor").value = sel.ref.color || "";
  }
  if (isAction) {
    $("selNum").value = sel.ref.order != null ? String(sel.ref.order) : "";
    // La liste « type » reflète le type de la flèche sélectionnée.
    if (sel.ref.type && Array.from($("arrowType").options).some((o) => o.value === sel.ref.type))
      $("arrowType").value = sel.ref.type;
  }
}
function toggleEditMode() {
  state.editing = !state.editing;
  $("editPalette").hidden = !state.editing;
  $("editToggle").classList.toggle("is-active", state.editing);
  state.editor.clearSelection();
  state.editor.setTool("select");
  setActiveTool("select");
  if (state.editing) {
    state.prevSimple = state.simpleMode;
    if (state.simpleMode) setSimple(false);     // tout voir pour éditer
  } else {
    setEditHint(null);
    if (state.prevSimple) setSimple(true);
  }
  // Bascule AUTOMATIQUE de la colonne de droite : ✏️ activé -> onglet Édition,
  // désactivé -> retour à la fiche (les onglets restent cliquables entre-temps).
  state.rightTab = state.editing ? "edit" : "peda";
  updateRightPanel();
  paint();
}
/* Remet l'atelier d'édition à zéro (sans toucher au schéma) : ferme le mode
   édition, vide la sélection, réinitialise le zoom. Appelé à chaque génération. */
function resetEditorChrome() {
  if (state.editing) { state.editing = false; $("editPalette").hidden = true; $("editToggle").classList.remove("is-active"); }
  if (state.editor) { state.editor.clearSelection(); state.editor.setTool("select"); }
  setActiveTool("select");
  $("deleteSelected").disabled = true;
  $("selProps").hidden = true; $("inspEmpty").hidden = false;
  $("selNumWrap").hidden = true; $("selRoleWrap").hidden = true; $("selTextWrap").hidden = true;
  $("selPlayerColorWrap").hidden = true;
  $("selZoneStyleWrap").hidden = true; $("selZoneColorWrap").hidden = true;
  $("duplicateSelected").hidden = true;
  $("coneColorWrap").hidden = true;
  state.rightTab = "peda";
  updateRightPanel();
  if (state.zoom !== 1) { state.zoom = 1; applyZoom(); }
  setEditHint(null);
}

/* ----------------------------------------------------------------------------
   ANNULER / RÉTABLIR — chaque geste d'édition validé (éditeur principal OU
   projection) enregistre un cliché ; la baseline est reposée à chaque nouvelle
   scène (génération, bibliothèque, schéma vierge, re-dessin « Compris »).
   -------------------------------------------------------------------------- */
function updateUndoUi() {
  $("undoBtn").disabled = !history.canUndo();
  $("redoBtn").disabled = !history.canRedo();
}
/* Après CHAQUE modification de la scène par un éditeur (commit) : compteurs,
   fiche (le Déroulement suit les flèches numérotées) et cliché d'historique. */
function onSceneChanged() {
  refreshMeta();
  renderPedagogy(state.lastJson || {});
  history.record(state.lastJson);
  updateUndoUi();
}
function applyHistorySnapshot(snap) {
  if (!snap) return;
  if (state.editor) state.editor.clearSelection();   // la sélection pointait l'ancienne scène
  closeFicheEditSilently();   // le formulaire montrerait des valeurs périmées
  state.lastJson = snap;
  refreshMeta();
  renderPedagogy(snap);
  updateUndoUi();
  paint();
}
const doUndo = () => applyHistorySnapshot(history.undo());
const doRedo = () => applyHistorySnapshot(history.redo());
/* Capture (export/impression) : on retire le halo de sélection du canevas. */
function cleanCapture(fn) {
  if (state.editor) { state.editor.clearSelection(); paint(); }
  fn();
}
function applyZoom() {
  const c = $("canvas");
  c.style.width = (state.zoom * 100) + "%";
  c.style.maxWidth = state.zoom > 1 ? "none" : "100%";
  $("zoomLabel").textContent = Math.round(state.zoom * 100) + "%";
}
function bumpZoom(delta) {
  state.zoom = Math.max(0.5, Math.min(2, Math.round((state.zoom + delta) * 100) / 100));
  applyZoom();
}
function printCurrent() {
  if (!state.lastJson) return showError("Génère d'abord un schéma à imprimer.");
  const img = getPngDataUrl();
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(state.lastJson.title || "Schéma")}</title>
    <style>body{font-family:system-ui,sans-serif;margin:24px;color:#0F172A}h1{font-size:20px}
    img{max-width:100%;border:1px solid #E6EBF1;border-radius:8px}
    .peda-section{margin-top:14px}.peda-section h3{font-size:12px;text-transform:uppercase;color:#64748B;margin:0 0 4px}
    .objectif{background:#E8F1FC;border-left:3px solid #1366D6;padding:8px 12px;border-radius:4px}
    ul{padding-left:18px;margin:4px 0}.peda-title{font-size:18px;font-weight:700}</style></head>
    <body><h1>${esc(state.lastJson.title || "Schéma tactique")}</h1>
    <img src="${img}"/>${pedagogyHtml(state.lastJson)}
    <script>window.onload=()=>{window.print();}<\/script></body></html>`);
  w.document.close();
}

/* Découpe un texte en lignes tenant dans maxW pour le rendu canvas. */
function wrapText(ctx, text, maxW) {
  const words = String(text).split(/\s+/);
  const lines = []; let cur = "";
  for (const w of words) {
    const test = cur ? cur + " " + w : w;
    if (ctx.measureText(test).width > maxW && cur) { lines.push(cur); cur = w; }
    else cur = test;
  }
  if (cur) lines.push(cur);
  return lines;
}
/* Construit la liste des lignes (texte/police/couleur) de la fiche pédagogique. */
function fichePedaLines(ctx, d, maxW) {
  const ped = d.pedagogy || {};
  const out = [];
  const push = (text, font, color, mt, indent = 0) => out.push({ text, font, color, mt, indent });
  const para = (text, font, color, mt, indent = 0) => {
    ctx.font = font;
    wrapText(ctx, text, maxW - indent).forEach((ln, i) => push(ln, font, color, i === 0 ? mt : 3, indent));
  };
  const header = (t) => push(t, "700 13px system-ui, sans-serif", "#64748B", 18);
  const F_BODY = "400 15px system-ui, sans-serif", C_BODY = "#1F2937";
  if (d.title) para(d.title, "700 22px system-ui, sans-serif", "#0F172A", 0);
  push(d.kind === "exercise" ? "Situation d'apprentissage" : "Schéma de match", "600 12px system-ui, sans-serif", "#0072B2", 4);
  if (ped.objectif) { header("🎯 OBJECTIF"); para(ped.objectif, F_BODY, C_BODY, 6); }
  if ((ped.organisation || []).length) { header("🛠️ ORGANISATION"); ped.organisation.forEach((it) => para("•  " + it, F_BODY, C_BODY, 6, 4)); }
  const steps = deroulementSteps(d);
  if (steps.length >= 2) { header("🧭 DÉROULEMENT"); steps.forEach((s, i) => para((i + 1) + ".  " + s, F_BODY, C_BODY, 6, 4)); }
  if ((ped.criteres_reussite || []).length) { header("✅ CRITÈRES DE RÉUSSITE"); ped.criteres_reussite.forEach((it) => para("•  " + it, F_BODY, "#15803D", 6, 4)); }
  if ((ped.erreurs || []).length) { header("⚠️ ERREURS À ÉVITER"); ped.erreurs.forEach((it) => para("•  " + it, F_BODY, "#B91C1C", 6, 4)); }
  return out;
}
/* Exporte une FICHE PNG : le schéma en haut, le contenu pédagogique dessous. */
function exportFiche() {
  if (!state.lastJson) return showError("Génère d'abord un schéma à exporter.");
  const d = state.lastJson;
  const img = new Image();
  img.onload = () => {
    const W = 1000, PAD = 36, LH = 21;
    const schemaH = Math.round(img.height * (W / img.width));
    const meas = document.createElement("canvas").getContext("2d");
    const lines = fichePedaLines(meas, d, W - PAD * 2);
    const pedaH = lines.length ? lines.reduce((h, l) => h + l.mt + LH, 0) + PAD * 2 : 0;
    const cv = document.createElement("canvas");
    cv.width = W; cv.height = schemaH + pedaH;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.drawImage(img, 0, 0, W, schemaH);
    if (pedaH) {
      ctx.strokeStyle = "#E6EBF1"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(0, schemaH + 0.5); ctx.lineTo(W, schemaH + 0.5); ctx.stroke();
      let y = schemaH + PAD;
      ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
      for (const l of lines) { y += l.mt + LH * 0.72; ctx.font = l.font; ctx.fillStyle = l.color; ctx.fillText(l.text, PAD + l.indent, y); y += LH * 0.28; }
    }
    const link = document.createElement("a");
    link.download = "fiche-" + (state.activeSport || "schema") + ".png";
    link.href = cv.toDataURL("image/png");
    link.click();
  };
  img.src = getPngDataUrl();
}
function closeExportMenu() { $("exportMenu").hidden = true; }

/* ----------------------------------------------------------------------------
   PARTAGE ENTRE COLLÈGUES — fichier TactiQ (.json) : la scène complète
   (schéma + fiche + analyse), ré-importable et MODIFIABLE, contrairement au
   PNG. L'import tolère aussi une scène brute (fichier data seul).
   -------------------------------------------------------------------------- */
function exportTactiqFile() {
  if (!state.lastJson) return showError("Génère d'abord un schéma à partager.");
  const payload = {
    app: "tactiq-schema", version: 1,
    title: state.lastJson.title || "Schéma tactique",
    sport: state.activeSport,
    kind: state.lastJson.kind || "match",
    data: state.lastJson,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "tactiq-" + (state.activeSport || "schema") + ".json";
  a.click();
  URL.revokeObjectURL(a.href);
}
async function importTactiqFile(file) {
  if (!file) return;
  try {
    const obj = JSON.parse(await file.text());
    const d = obj?.app === "tactiq-schema" ? obj.data : obj;   // tolère une scène brute
    if (!d || !Array.isArray(d.players)) throw new Error("players manquants");
    // Même circuit d'ouverture que la bibliothèque (openSchema), sans id :
    // le schéma importé est un NOUVEAU schéma chez ce compte.
    state.currentSchemaId = null;
    state.analysis = d?.meta?.analyse ? { analyse: d.meta.analyse, sport: obj.sport || d.sport, text: "" } : null;
    state.kind = d.kind === "exercise" ? "exercise" : "match";
    state.renderMode = d?.meta?.renderMode === "line" ? "line" : "color";
    $("prompt").value = "";
    selectByValue($("sportSelect"), obj.sport || d.sport);
    setSegActive("segKind", "data-kind", state.kind);
    setSegActive("segMode", "data-mode", state.renderMode);
    setRenderMode(state.renderMode);
    showView("editor");
    finishRender(d, "import");
  } catch (err) {
    await alertDialog({
      title: "Import impossible",
      message: "Ce fichier n'est pas un schéma TactiQ valide (" + (err?.message || err) + "). Utilise « 📦 Partager (fichier TactiQ) » pour produire un fichier compatible.",
    });
  }
}

async function saveCurrentSchema() {
  if (!state.lastJson) return showError("Génère d'abord un schéma avant d'enregistrer.");
  if (isTrial()) return showError("Mode essai : crée ton compte gratuit pour enregistrer tes schémas (30 générations/mois, 10 schémas stockés).");
  $("saveBtn").disabled = true;
  const row = {
    user_id: state.user?.id,   // exigé par la politique RLS (with check auth.uid() = user_id)
    title: state.lastJson.title || "Sans titre",
    kind: state.lastJson.kind || "match",
    sport: state.activeSport,
    data: state.lastJson,
  };
  const isNew = !state.currentSchemaId;
  try {
    if (state.currentSchemaId) {
      const { error } = await supabase.from("schemas").update(row).eq("id", state.currentSchemaId);
      if (error) throw error;
    } else {
      // Plafond (10 schémas, 30 pour un compte coordo-EPS) : au-delà, on
      // supprime le PLUS ANCIEN. Le trigger serveur applique la même règle.
      const limit = schemaLimit();
      let replaced = false;
      const { count } = await supabase.from("schemas").select("id", { count: "exact", head: true });
      if ((count || 0) >= limit) {
        const { data: oldest } = await supabase.from("schemas")
          .select("id").order("created_at", { ascending: true }).limit((count - limit) + 1);
        const ids = (oldest || []).map((o) => o.id);
        if (ids.length) { await supabase.from("schemas").delete().in("id", ids); replaced = true; }
      }
      const { data, error } = await supabase.from("schemas").insert(row).select("id").single();
      if (error) throw error;
      state.currentSchemaId = data.id;
      if (replaced) {
        showError(isCoordo()
          ? `Bibliothèque pleine (${limit} max) : le schéma le plus ancien a été remplacé.`
          : `Bibliothèque pleine (${limit} max) : le schéma le plus ancien a été remplacé. Un compte coordo-EPS donne droit à ${COORDO_SCHEMA_HINT} schémas stockés.`);
      }
      refreshQuota();   // le compteur « n/limite » de la bibliothèque suit
    }
    flashSaved();
    // PREMIER enregistrement (pas une mise à jour) : on propose de partager
    // la situation à la bibliothèque commune — opt-in, désactivable.
    if (isNew) proposeCommunityShare(true);
  } catch (err) {
    console.error(err);
    showError("Enregistrement impossible : " + (err?.message || err));
  } finally {
    $("saveBtn").disabled = false;
  }
}

/* ----------------------------------------------------------------------------
   BIBLIOTHÈQUE COMMUNE — proposer sa situation (pseudo + niveau 1..5).
   La proposition part en « pending » : elle n'apparaît qu'après validation
   (Dev Dashboard). Voir supabase/migrations/*_community_library.sql.
   -------------------------------------------------------------------------- */
const NIVEAU_LABEL = { 1: "découverte", 2: "apprentissage", 3: "consolidation", 4: "approfondissement", 5: "expertise" };
function communityShareDialog({ auto }) {
  return new Promise((resolve) => {
    const backdrop = document.createElement("div");
    backdrop.className = "modal-backdrop";
    backdrop.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true">
        <h3 class="modal__title">🌍 Partager à la bibliothèque commune ?</h3>
        <p class="modal__msg">Ta situation sera proposée aux autres enseignants (après validation). Ils pourront l'ouvrir et l'adapter — ton schéma personnel reste inchangé.</p>
        <label class="share-field">Ton pseudo (affiché comme auteur)
          <input id="sharePseudo" maxlength="30" placeholder="Ex : ProfEPS38" value="${esc(lsGet("tactiq_pseudo") || "")}">
        </label>
        <label class="share-field">Niveau de pratique visé
          <select id="shareNiveau">
            ${[1, 2, 3, 4, 5].map((n) => `<option value="${n}"${n === 2 ? " selected" : ""}>Niveau ${n} — ${NIVEAU_LABEL[n]}</option>`).join("")}
          </select>
        </label>
        ${auto ? `<label class="share-optout"><input type="checkbox" id="shareOptout"> Ne plus me le proposer à l'enregistrement</label>` : ""}
        <div class="modal__actions">
          <button class="ghost" id="shareCancel">Pas cette fois</button>
          <button class="primary" id="shareOk">Proposer</button>
        </div>
      </div>`;
    const finish = (share) => {
      const optout = !!backdrop.querySelector("#shareOptout")?.checked;
      const pseudo = backdrop.querySelector("#sharePseudo").value.trim();
      const niveau = parseInt(backdrop.querySelector("#shareNiveau").value, 10);
      backdrop.remove();
      document.removeEventListener("keydown", onKey);
      resolve({ share, pseudo, niveau, optout });
    };
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); finish(false); } };
    document.addEventListener("keydown", onKey);
    backdrop.addEventListener("mousedown", (e) => { if (e.target === backdrop) finish(false); });
    backdrop.querySelector("#shareCancel").addEventListener("click", () => finish(false));
    backdrop.querySelector("#shareOk").addEventListener("click", () => finish(true));
    document.body.appendChild(backdrop);
    requestAnimationFrame(() => backdrop.classList.add("is-open"));
  });
}
/* `source` optionnel : partager un schéma PRÉCIS (carte de « Mes schémas »)
   plutôt que celui ouvert dans l'éditeur. */
async function proposeCommunityShare(auto, source) {
  const src = source || (state.lastJson
    ? { id: state.currentSchemaId, title: state.lastJson.title, kind: state.lastJson.kind, sport: state.activeSport, data: state.lastJson }
    : null);
  if (!src || isTrial() || !state.user) return;
  if (auto && lsGet("tactiq_share_optout") === "1") return;
  // Déjà proposé (icône neutralisée) : garde-fou anti-doublon si on y arrive quand même.
  if (src.id && state.sharedSourceIds.has(src.id)) return;
  const res = await communityShareDialog({ auto });
  if (res.optout) lsSet("tactiq_share_optout", "1");
  if (!res.share) return;
  if (res.pseudo) lsSet("tactiq_pseudo", res.pseudo);
  const { error } = await supabase.from("community_schemas").insert({
    author_id: state.user.id,
    author_name: res.pseudo || "Collègue EPS",
    title: src.title || "Sans titre",
    kind: src.kind === "exercise" ? "exercise" : "match",
    sport: src.sport,
    niveau: res.niveau,
    data: src.data,
    source_schema_id: src.id || null,
    status: "pending",
  });
  if (error) { showError("Partage impossible : " + (error.message || error)); return; }
  // Neutralise l'icône de partage de ce schéma (déjà proposé) et rafraîchit la vue.
  if (src.id) {
    state.sharedSourceIds.add(src.id);
    if (state.libScope === "mine") renderLibrary();
  }
  await alertDialog({ title: "Merci pour le partage ! 🙌", message: "Ta situation a été proposée à la bibliothèque commune : elle y apparaîtra après validation." });
}

/* NOTIFICATION ADMIN — pastille sur le lien Dashboard : nombre de
   propositions en attente de validation. Rafraîchie à la connexion, toutes
   les 5 minutes, et après chaque décision de modération (onModerated). */
async function refreshPendingBadge() {
  const badge = $("dashPendingBadge");
  if (!badge) return;
  if (!state.isAdmin) { badge.hidden = true; return; }
  const { count, error } = await supabase
    .from("community_schemas")
    .select("id", { count: "exact", head: true })
    .eq("status", "pending");
  if (error) { badge.hidden = true; return; }
  badge.textContent = String(count || 0);
  badge.hidden = !(count > 0);
}
function flashSaved() {
  const b = $("saveBtn");
  const prev = b.dataset.label;
  b.classList.add("is-ok");
  b.querySelector(".save-label").textContent = "Enregistré ✓";
  setTimeout(() => { b.classList.remove("is-ok"); b.querySelector(".save-label").textContent = prev; }, 1600);
}

/* ============================================================================
   6) BIBLIOTHÈQUE « MES SCHÉMAS »
   ========================================================================== */
async function loadLibrary() {
  const grid = $("libGrid");
  grid.innerHTML = `<p class="lib-loading">Chargement…</p>`;
  if (state.libScope === "community") {
    // Bibliothèque COMMUNE : situations approuvées (RLS), plus récentes d'abord.
    const { data, error } = await supabase
      .from("community_schemas")
      .select("id,title,kind,sport,niveau,author_name,data,created_at")
      .eq("status", "approved")
      .order("created_at", { ascending: false })
      .limit(120);
    if (error) {
      grid.innerHTML = `<p class="lib-loading">Bibliothèque commune indisponible (${esc(error.message)}).</p>`;
      return;
    }
    state.communityRows = data || [];
    renderLibrary();
    return;
  }
  const { data, error } = await supabase
    .from("schemas")
    .select("id,title,kind,sport,data,updated_at")
    .order("updated_at", { ascending: false });
  if (error) {
    grid.innerHTML = `<p class="lib-loading">Impossible de charger la bibliothèque (${esc(error.message)}).</p>`;
    return;
  }
  state.libraryRows = data || [];
  // Schémas DÉJÀ proposés à la bibliothèque commune (en attente ou publiés) :
  // leur icône de partage sera neutralisée. Un refus reste re-proposable.
  state.sharedSourceIds = new Set();
  if (state.user) {
    const { data: shared } = await supabase
      .from("community_schemas")
      .select("source_schema_id")
      .eq("author_id", state.user.id)
      .in("status", ["pending", "approved"]);
    (shared || []).forEach((s) => s.source_schema_id && state.sharedSourceIds.add(s.source_schema_id));
  }
  renderLibrary();
}

/* Texte balayé par la recherche libre : titre + auteur + TOUTE la fiche
   pédagogique (objectif, organisation, critères de réussite, erreurs), pour
   qu'un mot rédigé dans la fiche remonte la situation. Vaut pour « Mes
   schémas » comme pour la bibliothèque commune (les deux embarquent `data`). */
function librarySearchText(r) {
  const ped = r.data?.pedagogy || {};
  const parts = [r.title, r.author_name, ped.objectif]
    .concat(ped.organisation || [], ped.criteres_reussite || [], ped.erreurs || []);
  return parts.filter(Boolean).join(" ").toLowerCase();
}

function renderLibrary() {
  const community = state.libScope === "community";
  const rows = community ? state.communityRows : state.libraryRows;
  const ex = rows.filter((r) => r.kind === "exercise").length;
  const mt = rows.length - ex;
  $("libCount").textContent = community
    ? `${rows.length} situation${rows.length > 1 ? "s" : ""} partagée${rows.length > 1 ? "s" : ""} par les collègues · ${ex} exercice${ex > 1 ? "s" : ""}, ${mt} phase${mt > 1 ? "s" : ""} de match`
    : `${rows.length}/${schemaLimit()} schéma${rows.length > 1 ? "s" : ""} · ${ex} exercice${ex > 1 ? "s" : ""}, ${mt} phase${mt > 1 ? "s" : ""} de match`
      + (isCoordo() ? " · quota étendu grâce à ton compte coordo-EPS" : "");

  const q = $("librarySearch").value.trim().toLowerCase();
  const filter = $("segFilter").querySelector(".is-active")?.getAttribute("data-filter") || "all";
  const sport = $("libSportFilter").value;
  const niveau = $("libNiveauFilter").value;
  const shown = rows.filter((r) => {
    if (q && !librarySearchText(r).includes(q)) return false;
    if (filter === "match" && r.kind !== "match") return false;
    if (filter === "exercise" && r.kind !== "exercise") return false;
    if (sport !== "all" && r.sport !== sport) return false;
    if (community && niveau !== "all" && String(r.niveau || "") !== niveau) return false;
    return true;
  });

  const grid = $("libGrid");
  if (!shown.length) {
    grid.innerHTML = `<p class="lib-loading">${rows.length
      ? "Aucun schéma ne correspond aux filtres."
      : community
        ? "Aucune situation partagée pour l'instant — propose la tienne à l'enregistrement d'un schéma (🌍) !"
        : "Aucun schéma enregistré pour l'instant. Génère puis enregistre ton premier schéma !"}</p>`;
    return;
  }
  grid.innerHTML = "";
  shown.forEach((r) => grid.appendChild(buildCard(r, community)));
}

function fmtDate(iso) {
  try { return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" }).format(new Date(iso)); }
  catch (_) { return ""; }
}
const SPORT_LABEL = { football: "Football", basketball: "Basket-ball", basket3x3: "Basket 3×3", rugby: "Rugby", handball: "Handball", futsal: "Futsal", volleyball: "Volley-ball", badminton: "Badminton", tennis: "Tennis", tennis_de_table: "Tennis de table", ultimate: "Ultimate", gymnastique: "Gymnastique", athletisme: "Athlétisme", tir_a_l_arc: "Tir à l'arc", petanque: "Pétanque", other: "Autre" };
const sportLabel = (s) => SPORT_LABEL[s] || s || "";
// Icône corbeille partagée par les boutons de suppression (schéma perso + retrait admin).
const TRASH_SVG = `<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;

function buildCard(r, community = false) {
  const isEx = r.kind === "exercise";
  const card = document.createElement("article");
  card.className = "sch-card";

  const thumb = document.createElement("div");
  thumb.className = "sch-thumb";
  const tc = document.createElement("canvas");
  tc.width = 420; tc.height = 270;
  thumb.appendChild(tc);
  renderToCanvas(tc, r.data, r.sport, r.data?.meta?.renderMode || "color", { compact: true });

  const body = document.createElement("div");
  body.className = "sch-body";
  const meta = community
    ? `${r.niveau ? `<span class="badge-niveau" title="Niveau ${r.niveau} — ${NIVEAU_LABEL[r.niveau] || ""}">N${r.niveau}</span> · ` : ""}par ${esc(r.author_name || "un collègue")} · ${fmtDate(r.created_at)}`
    : fmtDate(r.updated_at);
  body.innerHTML = `
    <div class="sch-row">
      <span class="kind-pill ${isEx ? "kind-exercise" : "kind-match"}">${isEx ? "Exercice" : "Match"}</span>
      <span class="badge-sport">${esc(SPORT_LABEL[r.sport] || r.sport || "")}</span>
    </div>
    <h4>${esc(r.title || "Sans titre")}</h4>
    <div class="sch-meta">${meta}</div>`;

  card.appendChild(thumb);
  card.appendChild(body);
  if (!community) {
    if (state.sharedSourceIds.has(r.id)) {
      // DÉJÀ proposé à la bibliothèque commune : icône neutralisée (non
      // cliquable) pour éviter les doublons ; un simple repère visuel.
      const shared = document.createElement("span");
      shared.className = "sch-share sch-share--done";
      shared.title = "Déjà proposée à la bibliothèque commune";
      shared.setAttribute("aria-label", `« ${r.title || "Sans titre"} » a déjà été proposée à la bibliothèque commune`);
      shared.textContent = "✓";
      card.appendChild(shared);
    } else {
      // Proposer CE schéma à la bibliothèque commune, sans avoir à l'ouvrir.
      const share = document.createElement("button");
      share.className = "sch-share iconbtn";
      share.title = "Proposer à la bibliothèque commune";
      share.setAttribute("aria-label", `Proposer « ${r.title || "Sans titre"} » à la bibliothèque commune`);
      share.textContent = "🌍";
      share.addEventListener("click", (e) => {
        e.stopPropagation();
        proposeCommunityShare(false, { id: r.id, title: r.title, kind: r.kind, sport: r.sport, data: r.data });
      });
      card.appendChild(share);
    }
    const del = document.createElement("button");
    del.className = "sch-del iconbtn";
    del.title = "Supprimer ce schéma";
    del.setAttribute("aria-label", `Supprimer le schéma « ${r.title || "Sans titre"} »`);
    del.innerHTML = TRASH_SVG;
    del.addEventListener("click", (e) => { e.stopPropagation(); deleteSchema(r); });
    card.appendChild(del);
  } else if (state.isAdmin) {
    // Bibliothèque commune : l'admin peut RETIRER une situation approuvée
    // directement depuis la vignette (RLS `community_remove`). L'ouverture se
    // faisant en COPIE, ce retrait ne touche aucun schéma personnel.
    const del = document.createElement("button");
    del.className = "sch-del iconbtn";
    del.title = "Retirer de la bibliothèque commune (admin)";
    del.setAttribute("aria-label", `Retirer « ${r.title || "Sans titre"} » de la bibliothèque commune`);
    del.innerHTML = TRASH_SVG;
    del.addEventListener("click", (e) => { e.stopPropagation(); deleteCommunitySchema(r); });
    card.appendChild(del);
  }
  // Situation commune : ouverte en COPIE (nouveau schéma chez soi, l'original
  // reste intact) ; schéma personnel : ouverture normale.
  card.addEventListener("click", () => openSchema(r, { copy: community }));
  return card;
}

async function deleteSchema(r) {
  const ok = await confirmDialog({
    title: "Supprimer ce schéma ?",
    message: `« ${r.title || "Sans titre"} » sera définitivement supprimé de ta bibliothèque. Cette action est irréversible.`,
    confirmLabel: "Supprimer",
    danger: true,
  });
  if (!ok) return;
  const { error } = await supabase.from("schemas").delete().eq("id", r.id);
  if (error) { await alertDialog({ title: "Suppression impossible", message: error.message }); return; }
  if (state.currentSchemaId === r.id) state.currentSchemaId = null;
  state.libraryRows = state.libraryRows.filter((x) => x.id !== r.id);
  renderLibrary();
}

/* RETRAIT ADMIN d'une situation de la bibliothèque commune. La RLS
   `community_remove` n'autorise le delete qu'à l'admin (ou à l'auteur) ; côté
   interface le bouton n'apparaît que pour l'admin (state.isAdmin). Le retrait
   est définitif et concerne TOUS les collègues — d'où la confirmation. */
async function deleteCommunitySchema(r) {
  const ok = await confirmDialog({
    title: "Retirer de la bibliothèque commune ?",
    message: `« ${r.title || "Sans titre"} » (proposé par ${r.author_name || "un collègue"}) sera retiré de la bibliothèque commune pour tous les enseignants. Cette action est irréversible.`,
    confirmLabel: "Retirer",
    danger: true,
  });
  if (!ok) return;
  const { error } = await supabase.from("community_schemas").delete().eq("id", r.id);
  if (error) { await alertDialog({ title: "Retrait impossible", message: error.message }); return; }
  state.communityRows = state.communityRows.filter((x) => x.id !== r.id);
  renderLibrary();
}

function openSchema(r, { copy = false } = {}) {
  const d = r.data || {};
  // Copie (bibliothèque commune) : le schéma s'ouvre comme un NOUVEAU schéma
  // — l'enregistrer l'ajoute à « Mes schémas » sans toucher l'original partagé.
  state.currentSchemaId = copy ? null : r.id;
  state.lastJson = d;
  // Schéma né d'une analyse structurée : le panneau « Ce que j'ai compris »
  // se restaure (re-dessin local possible, sans quota).
  state.analysis = d?.meta?.analyse ? { analyse: d.meta.analyse, sport: r.sport, text: "" } : null;
  state.kind = d.kind === "exercise" ? "exercise" : "match";
  state.renderMode = d?.meta?.renderMode === "line" ? "line" : "color";
  // Reflète les métadonnées dans le formulaire.
  $("prompt").value = "";
  selectByValue($("sportSelect"), r.sport);
  setSegActive("segKind", "data-kind", state.kind);
  setSegActive("segMode", "data-mode", state.renderMode);
  setRenderMode(state.renderMode);
  showView("editor");
  finishRender(d, "edge");
}
function selectByValue(sel, value) {
  const ok = Array.from(sel.options).some((o) => o.value === value);
  sel.value = ok ? value : sel.value;
}

function newSchema() {
  state.currentSchemaId = null;
  state.analysis = null;
  resetEditorChrome();
  clearError();
  $("prompt").value = "";
  // Scène VIERGE éditable : on peut composer entièrement à la main (palette).
  const sport = resolveSport(null);
  const blank = { kind: state.kind, sport, title: "Nouveau schéma", players: [], actions: [], zones: [], markers: [], pedagogy: {}, meta: { renderMode: state.renderMode } };
  finishRender(blank, "manual");
  showView("editor");
}

/* ============================================================================
   7) NAVIGATION ENTRE VUES
   ========================================================================== */
function showView(view) {
  state.view = view;
  $("editorView").hidden = view !== "editor";
  $("libraryView").hidden = view !== "library";
  $("dashView").hidden = view !== "dash";
  $("navEditor").classList.toggle("is-active", view === "editor");
  $("navLibrary").classList.toggle("is-active", view === "library");
  $("navDash").classList.toggle("is-active", view === "dash");
  if (view === "library") loadLibrary();
  if (view === "dash") renderDashboard({
    sportLabel,
    onAdsChanged: refreshAds,
    // « Examiner » : ouvre la proposition (schéma + fiche) dans l'éditeur, en
    // copie — l'original en attente reste intact, la décision se prend au
    // retour sur le Dashboard.
    openSchema: (row) => openSchema(row, { copy: true }),
    onModerated: refreshPendingBadge,
  });
  // Écran d'attente : actif uniquement sur l'éditeur vierge.
  if (view === "editor" && !state.lastJson) startIdle(); else stopIdle();
}

/* Active un bouton dans un groupe segmenté. */
function setSegActive(segId, attr, value) {
  $(segId).querySelectorAll("button").forEach((b) => {
    b.classList.toggle("is-active", b.getAttribute(attr) === value);
  });
}

/* ----------------------------------------------------------------------------
   SIDEBAR D'AIDE : non-modale (n'empêche pas les clics), ancrable gauche/droite
   (préférence mémorisée), rappel discret jusqu'à la première ouverture.
   -------------------------------------------------------------------------- */
const lsGet = (k) => { try { return localStorage.getItem(k); } catch (_) { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch (_) { /* ignore */ } };
function initHelp() {
  const panel = $("helpPanel");
  // Deux déclencheurs : le bouton « ? » du header (desktop) et l'onglet « Aide »
  // de la barre du bas (mobile). Les deux basculent le même panneau.
  const triggers = [$("helpBtn"), $("navHelp")].filter(Boolean);
  const navHelp = $("navHelp");
  const left = lsGet("tactiq_help_side") === "left";
  panel.classList.toggle("dock-left", left);
  panel.classList.toggle("dock-right", !left);
  if (lsGet("tactiq_help_seen") !== "1") triggers.forEach((t) => t.classList.add("pulse"));

  const open = () => {
    panel.classList.add("is-open");
    triggers.forEach((t) => t.classList.remove("pulse"));
    if (navHelp) navHelp.classList.add("is-active");   // onglet actif tant que l'aide est ouverte
    lsSet("tactiq_help_seen", "1");
  };
  const close = () => {
    panel.classList.remove("is-open");
    if (navHelp) navHelp.classList.remove("is-active");
  };
  triggers.forEach((t) => t.addEventListener("click", () => panel.classList.contains("is-open") ? close() : open()));
  $("helpClose").addEventListener("click", close);
  $("helpDock").addEventListener("click", () => {
    const nowLeft = panel.classList.toggle("dock-left");
    panel.classList.toggle("dock-right", !nowLeft);
    lsSet("tactiq_help_side", nowLeft ? "left" : "right");
  });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape" && panel.classList.contains("is-open")) close(); });
}

/* ----------------------------------------------------------------------------
   POPOVER QUOTA : détail du groupe quota de l'en-tête. Les paliers responsive
   replient l'e-mail (< 1440 px) et la phrase « quota étendu… » (pastille ★) —
   ce panneau les rend accessibles partout, tactile compris (un `title` ne se
   survole pas au doigt). Motif « disclosure » : bouton aria-expanded +
   panneau frère ; contenu rempli à l'ouverture depuis l'état courant.
   -------------------------------------------------------------------------- */
function initQuotaPopover() {
  const btn = $("quotaGroupBtn"), pop = $("quotaPopover");
  const close = () => { pop.hidden = true; btn.setAttribute("aria-expanded", "false"); };
  const open = () => {
    const q = state.quota;
    pop.querySelector('[data-pop="usage"]').textContent = q
      ? `${Number(q.used) || 0} / ${Number(q.quota) || 0} générations ce mois-ci`
      : "Quota en cours de chargement…";
    pop.querySelector('[data-pop="coordo"]').hidden = !isCoordo();
    pop.querySelector('[data-pop="account"]').textContent =
      isTrial() ? "Mode essai" : (state.user?.email || "");
    pop.hidden = false; btn.setAttribute("aria-expanded", "true");
  };
  btn.addEventListener("click", () => (pop.hidden ? open() : close()));
  // Clic hors du panneau (le clic sur le bouton est déjà géré ci-dessus).
  document.addEventListener("click", (e) => {
    if (!pop.hidden && !pop.contains(e.target) && !btn.contains(e.target)) close();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !pop.hidden) { close(); btn.focus(); }
  });
}

/* ============================================================================
   8) INITIALISATION & ÉVÉNEMENTS
   ========================================================================== */
function init() {
  initRenderer($("canvas"));
  setRenderMode(state.renderMode);
  startIdle();   // écran d'attente : terrains qui défilent (au lieu du foot figé)
  state.editor = createEditor($("canvas"), {
    getScene: () => state.lastJson,
    getView: () => state.sceneView,
    repaint: paint,
    onChange: onSceneChanged,
    setHint: setEditHint,
    onSelectionChange: onEditorSelection,
    onToolChange: setActiveTool,
    isEditing: () => state.editing,
    // Élément à texte posé (bulle, texte libre, cote) : le champ Texte prend
    // le focus, texte par défaut pré-sélectionné — on écrit immédiatement.
    onElementAdded: (ref) => {
      if (!hasEditableText(ref)) return;
      requestAnimationFrame(() => { const t = $("selText"); t.focus(); t.select(); });
    },
  });
  setEditHint(null);
  updateArrowTypeOptions();   // menu des flèches aligné sur le sport par défaut
  applyZoom();
  initUnderstanding(applyUnderstanding);
  initHelp();
  initQuotaPopover();

  // --- Auth ---
  $("authSubmit").addEventListener("click", handleAuthSubmit);
  $("authPassword").addEventListener("keydown", (e) => { if (e.key === "Enter") handleAuthSubmit(); });
  $("authToggleBtn").addEventListener("click", () => setAuthMode(state.authMode === "signin" ? "signup" : "signin"));
  $("forgotLink").addEventListener("click", handleForgotPassword);
  $("magicLinkBtn").addEventListener("click", handleMagicLink);
  // Écran d'erreur SSO : retour au parcours de connexion normal.
  $("ssoLoginBtn").addEventListener("click", () => location.replace("/"));
  $("logoutBtn").addEventListener("click", () => (isTrial() ? trialToSignup() : handleLogout()));
  $("exportAiBtn").addEventListener("click", exportAiAnalysis);
  $("trialBtn").addEventListener("click", startTrial);
  $("trialSignupBtn").addEventListener("click", trialToSignup);
  setAuthMode("signin");

  // --- Navigation ---
  $("navEditor").addEventListener("click", () => showView("editor"));
  $("navLibrary").addEventListener("click", () => showView("library"));
  $("navDash").addEventListener("click", () => showView("dash"));
  $("newSchemaBtn").addEventListener("click", newSchema);

  // --- Segmenté Match / Exercice ---
  $("segKind").querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => {
      state.kind = b.getAttribute("data-kind");
      state.kindTouched = true;
      setSegActive("segKind", "data-kind", state.kind);
    }));
  setSegActive("segKind", "data-kind", state.kind);

  // --- Génération ---
  $("generateBtn").addEventListener("click", generate);
  initFeedbackBar();
  // « Tout remettre à zéro » = remise à zéro COMPLÈTE : sport rendu à la
  // détection automatique et type à l'auto-détection — contrairement à
  // « Créer à la main » et « Nouveau schéma », qui respectent le sport choisi.
  $("resetBtn").addEventListener("click", () => {
    $("sportSelect").value = "auto";
    state.kind = "exercise";
    state.kindTouched = false;
    setSegActive("segKind", "data-kind", state.kind);
    newSchema();
  });
  // Parcours SANS IA : terrain vierge + mode édition ouvert directement.
  $("blankBtn").addEventListener("click", () => {
    newSchema();                          // scène vierge (ferme l'atelier d'édition)
    if (!state.editing) toggleEditMode(); // ... qu'on rouvre aussitôt, prêt à composer
    setEditHint("Terrain vierge : choisis le sport à gauche (le terrain suit), pose joueurs et éléments, délimite des zones, trace des flèches.");
  });
  $("sportSelect").addEventListener("change", () => {
    // Le sport choisi pilote le terrain ET le menu des flèches, même AVANT toute
    // génération (composition à la main d'un schéma vierge).
    state.activeSport = resolveSport(state.lastJson ? state.lastJson.sport : null);
    updateArrowTypeOptions();
    // La scène suit le terrain affiché : cohérent à l'enregistrement et à la
    // réouverture depuis la bibliothèque.
    if (state.lastJson) { state.lastJson.sport = state.activeSport; refreshMeta(); paint(); }
  });
  document.querySelectorAll("[data-example]").forEach((btn) =>
    btn.addEventListener("click", () => {
      $("prompt").value = btn.getAttribute("data-example");
      // L'exemple impose son type (Match/Exercice) : on cale le segmenté dessus
      // pour que la génération respecte l'intention de l'exemple. Sans data-kind,
      // on laisse la détection automatique du texte décider.
      const k = btn.getAttribute("data-kind");
      if (k === "match" || k === "exercise") {
        state.kind = k; state.kindTouched = true; setSegActive("segKind", "data-kind", k);
      } else {
        state.kindTouched = false;
      }
      generate();
    }));

  // --- Barre d'outils ---
  $("segMode").querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => setMode(b.getAttribute("data-mode"))));
  setSegActive("segMode", "data-mode", state.renderMode);
  $("segSimple").querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => setSimple(b.getAttribute("data-simple") === "simple")));
  setSegActive("segSimple", "data-simple", state.simpleMode ? "simple" : "detail");
  $("zoomOut").addEventListener("click", () => bumpZoom(-0.25));
  $("zoomIn").addEventListener("click", () => bumpZoom(0.25));
  $("printBtn").addEventListener("click", () => { closeExportMenu(); cleanCapture(printCurrent); });
  $("saveBtn").addEventListener("click", saveCurrentSchema);

  // --- Mode projection : plein écran, diaporama des étapes (order des actions)
  //     OU « Schéma complet » modifiable en direct. Toujours la scène COMPLÈTE
  //     (lastJson) : les modifications en projection portent sur la scène
  //     réelle — conservées à la fermeture, incluses à l'enregistrement.
  state.projection = createProjection({
    getScene: () => state.lastJson,
    getSport: () => state.activeSport,
    getMode: () => state.renderMode,
    onSceneEdited: onSceneChanged,
    onClose: () => { if (state.lastJson) { refreshMeta(); paint(); } },
  });
  $("projectBtn").addEventListener("click", () => {
    if (!state.lastJson) return showError("Génère d'abord un schéma à projeter.");
    state.projection.open();
  });

  // --- Éditeur complet : toggle + palette (sélection d'abord) ---
  $("editToggle").addEventListener("click", toggleEditMode);
  $("editPalette").querySelectorAll("[data-tool]").forEach((b) =>
    b.addEventListener("click", () => { const t = b.getAttribute("data-tool"); state.editor.setTool(t); setActiveTool(t); }));
  $("arrowType").addEventListener("change", (e) => state.editor.setActionType(e.target.value));
  $("coneColor").addEventListener("change", (e) => state.editor.setConeColor(e.target.value));
  $("selNum").addEventListener("input", (e) => state.editor.setSelectedNumber(e.target.value));
  $("selRole").addEventListener("input", (e) => state.editor.setSelectedRole(e.target.value));
  $("selText").addEventListener("input", (e) => {
    state.editor.setSelectedText(e.target.value);
    // L'en-tête de l'inspecteur (« ▭ Zone — étiquette ») suit la saisie.
    $("selKind").textContent = selKindLabel(state.editor.getSelection());
  });
  $("selZoneStyle").addEventListener("change", (e) => state.editor.setSelectedZoneStyle(e.target.value));
  $("selZoneColor").addEventListener("change", (e) => state.editor.setSelectedZoneColor(e.target.value));
  $("selPlayerColor").addEventListener("change", (e) => state.editor.setSelectedPlayerColor(e.target.value));
  $("duplicateSelected").addEventListener("click", () => state.editor.duplicateSelection());
  $("deleteSelected").addEventListener("click", () => state.editor.deleteSelection());
  $("undoBtn").addEventListener("click", doUndo);
  $("redoBtn").addEventListener("click", doRedo);

  // --- Colonne de droite : onglets Fiche / Édition + rapatriement des blocs
  //     d'édition (propriétés, bibliothèque) selon la largeur d'écran ---
  $("rightTabs").querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => { state.rightTab = b.getAttribute("data-rtab"); updateRightPanel(); }));
  // Édition de la fiche pédagogique (délégation : le formulaire est reconstruit).
  $("pedaEditBtn").addEventListener("click", toggleFicheEdit);
  $("pedaContent").addEventListener("input", onFicheInput);
  $("pedaContent").addEventListener("click", onFicheClick);
  const onBreakpoint = () => relocateEditorBlocks();
  if (deskMq.addEventListener) deskMq.addEventListener("change", onBreakpoint);
  else if (deskMq.addListener) deskMq.addListener(onBreakpoint);   // anciens Safari
  relocateEditorBlocks();

  // Raccourcis clavier du mode édition (hors champs de saisie, hors projection
  // — l'overlay de projection a ses propres raccourcis) :
  //   Suppr/Retour = supprimer · Ctrl+Z / Ctrl+Maj+Z / Ctrl+Y = annuler/rétablir
  //   Échap = retour Sélection puis désélection · flèches = déplacement fin.
  document.addEventListener("keydown", (e) => {
    if (!state.editing || state.view !== "editor" || !$("projection").hidden) return;
    const tag = (document.activeElement && document.activeElement.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); (e.shiftKey ? doRedo : doUndo)(); return; }
    if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); doRedo(); return; }
    if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); state.editor.duplicateSelection(); return; }
    if (mod || e.altKey) return;
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); state.editor.deleteSelection(); return; }
    if (e.key === "Escape") {
      if ($("helpPanel").classList.contains("is-open")) return;   // Échap ferme d'abord l'aide
      if (state.editor.getTool() !== "select") { state.editor.setTool("select"); setActiveTool("select"); }
      else if (state.editor.getSelection()) { state.editor.clearSelection(); paint(); }
      return;
    }
    const NUDGE = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (NUDGE[e.key] && state.editor.getSelection()) {
      e.preventDefault();
      const s = e.shiftKey ? 3 : 1;
      state.editor.nudgeSelection(NUDGE[e.key][0] * s, NUDGE[e.key][1] * s);
    }
  });

  // --- Menu d'export (schéma seul / fiche complète) — capture SANS halo ---
  $("exportBtn").addEventListener("click", (e) => { e.stopPropagation(); $("exportMenu").hidden = !$("exportMenu").hidden; });
  $("exportPngBtn").addEventListener("click", () => { closeExportMenu(); cleanCapture(() => exportPng(state.activeSport)); });
  $("exportFicheBtn").addEventListener("click", () => { closeExportMenu(); cleanCapture(exportFiche); });
  document.addEventListener("click", (e) => { if (!e.target.closest(".export-wrap")) closeExportMenu(); });

  // --- Partage : fichier TactiQ (.json) exportable / importable ---
  $("exportJsonBtn").addEventListener("click", () => { closeExportMenu(); exportTactiqFile(); });
  $("importMenuBtn").addEventListener("click", () => { closeExportMenu(); $("importFile").click(); });
  $("importBtn").addEventListener("click", () => $("importFile").click());
  $("importFile").addEventListener("change", (e) => {
    importTactiqFile(e.target.files && e.target.files[0]);
    e.target.value = "";   // permet de ré-importer le même fichier
  });

  // --- Filtres bibliothèque ---
  $("librarySearch").addEventListener("input", renderLibrary);
  $("libSportFilter").addEventListener("change", renderLibrary);
  $("libNiveauFilter").addEventListener("change", renderLibrary);
  $("segFilter").querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => { setSegActive("segFilter", "data-filter", b.getAttribute("data-filter")); renderLibrary(); }));
  // Bascule Mes schémas / Bibliothèque commune (le filtre niveau n'a de sens
  // que côté commune).
  $("segLibScope").querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => {
      state.libScope = b.getAttribute("data-scope");
      setSegActive("segLibScope", "data-scope", state.libScope);
      $("libNiveauFilter").hidden = state.libScope !== "community";
      loadLibrary();
    }));
  // Proposition manuelle depuis le menu ⋯ de l'éditeur.
  $("shareCommunityBtn").addEventListener("click", () => { closeExportMenu(); proposeCommunityShare(false); });

  // --- Session ---
  // Route SSO (/sso?token_hash=…) : on arme le flux AVANT toute restauration
  // de session pour que la landing ne s'affiche pas pendant l'échange.
  if (isSsoRoute()) state.ssoPending = true;
  // Lien de réinitialisation : l'URL porte « type=recovery ». On arme le flux
  // AVANT toute restauration de session, pour ne pas ouvrir l'app par erreur.
  if (/type=recovery/.test(location.hash)) state.recovering = true;
  if (isSsoRoute()) handleSsoEntry();
  else supabase.auth.getSession().then(({ data }) => applySession(data.session));
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === "PASSWORD_RECOVERY") state.recovering = true;
    setTimeout(() => applySession(session), 0);
  });

  // Notification admin : re-comptage périodique des propositions en attente
  // (requête head/count très légère ; sans effet pour les non-admins).
  setInterval(refreshPendingBadge, 5 * 60 * 1000);

  if (import.meta.env.DEV) window.sb = supabase;

  // --- PWA : service worker (en production uniquement — le cache gênerait le dev) ---
  // ⚠️ LEÇON DE LA SALVE DU 09/08, qui a coûté un lot de tests entier. Le
  // service worker sert bien le RÉSEAU d'abord : le code déployé arrive au
  // prochain chargement. Mais une PWA INSTALLÉE reste ouverte des jours, et
  // une fenêtre ouverte exécute indéfiniment le JS chargé à son lancement.
  // Résultat : un lot de 👎 a jugé une version antérieure de plusieurs
  // semaines — sept règles de fidélité manquaient à l'appel, dont celles qui
  // attrapaient précisément les deux défauts jugés « disqualifiants ».
  // On DÉTECTE donc l'arrivée d'une nouvelle version et on invite à recharger,
  // sans jamais forcer : un rechargement d'office effacerait un schéma en
  // cours d'édition.
  if ("serviceWorker" in navigator && import.meta.env.PROD) {
    window.addEventListener("load", async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js");
        const proposerMaj = () => {
          const chip = $("updateChip");
          if (chip) { chip.hidden = false; return; }
          // Repli si le balisage n'est pas là : bandeau minimal, même rôle.
          const el = document.createElement("button");
          el.type = "button";
          el.textContent = "🔄 Nouvelle version disponible — recharger";
          el.style.cssText = "position:fixed;left:50%;transform:translateX(-50%);bottom:16px;z-index:9999;"
            + "padding:10px 16px;border:0;border-radius:999px;cursor:pointer;font:600 14px system-ui,sans-serif;"
            + "background:#0F172A;color:#fff;box-shadow:0 6px 24px rgba(0,0,0,.3)";
          el.addEventListener("click", () => location.reload());
          document.body.appendChild(el);
        };
        // Une version est déjà en attente (onglet rouvert après un déploiement).
        if (reg.waiting) proposerMaj();
        reg.addEventListener("updatefound", () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener("statechange", () => {
            // `controller` non nul = ce n'est PAS la première installation :
            // il y a bien une version PRÉCÉDENTE à remplacer.
            if (sw.state === "installed" && navigator.serviceWorker.controller) proposerMaj();
          });
        });
        // Une PWA laissée ouverte ne revérifie pas d'elle-même : on regarde au
        // retour au premier plan, et au plus une fois par heure.
        let dernier = 0;
        const verifier = () => {
          if (document.hidden || Date.now() - dernier < 3600_000) return;
          dernier = Date.now();
          reg.update().catch(() => {});
        };
        document.addEventListener("visibilitychange", verifier);
        setInterval(verifier, 15 * 60_000);
      } catch (_) { /* le service worker n'est jamais bloquant */ }
    });
  }
}

init();
